// src/views/ProvenanceHome.js
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import AuditChainChase from "./AuditChainChase";
import GeolocationsMap from "./GeolocationsMap";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Analytics,
  BugReport,
  Clear,
  Close,
  ContentCopy,
  FilterAlt,
  FilterAltOff,
  GetApp,
  Insights,
  Refresh,
  Search,
  TableChart,
  Visibility,
  Wifi,
  WifiOff,
  PictureAsPdf,
  AdminPanelSettings,
  Person,
  Gavel,
  CheckCircle,
  Error as ErrorIcon,
  HourglassTop,
  Public,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { ethers, AbiCoder, getAddress, formatUnits, keccak256, toUtf8Bytes } from "ethers";
import { useLocation } from "react-router-dom";

import carbonAbi from "../abis/Carbon.json";
import energyAbi from "../abis/ErgonEnergy.json";
import { buildHepekProvenanceViewModel, toArray } from "../utils/hepekProvenanceAdapter";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

// Contexts (exported)
export const LogContext = createContext({ log: [], addLog: () => { } });
export const ProviderContext = createContext(null);


function VerifyActionButton({
  status,            // null | "pending" | true | false | "unknown" | "error"
  children,
  ...btnProps
}) {
  let badge = null;

  if (status === "pending") {
    badge = <CircularProgress size={12} />;
  } else if (status === true) {
    badge = <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />;
  } else if (status === false) {
    badge = <CancelIcon fontSize="small" sx={{ color: "error.main" }} />;
  } else if (status === "unknown") {
    badge = <HelpOutlineIcon fontSize="small" sx={{ color: "text.disabled" }} />;
  } else if (status === "error") {
    badge = <CancelIcon fontSize="small" sx={{ color: "error.main" }} />;
  }

  return (
    <Badge
      overlap="rectangular"
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      badgeContent={badge}
      sx={{
        "& .MuiBadge-badge": {
          background: "transparent",
          minWidth: 0,
          height: "auto",
          right: 1,
          top: 0,
          padding: 0,
        },
      }}
    >
      <Button {...btnProps}>{children}</Button>
    </Badge>
  );
}


/**
 * Compute readingHash exactly like Solidity:
 * keccak256(abi.encode(READING_V2_DOMAIN, hepekID, bucketTs, dir, produced, export, consumed, factor, prevHash))
 */
export function computeReadingHashV2({
  hepek,        // address
  bucketTs,     // uint256 (epoch seconds, minute bucket)
  dir,          // uint8: 1=OUT, 2=IN, 0=GENESIS
  produced,     // uint256
  exported,     // uint256
  consumed,     // uint256
  factor,       // uint256 (even if contract stores uint8, it is encoded as uint256 in abi.encode call if you pass uint256)
  prevHash      // bytes32 (0x...)
}) {
  const hepekAddr = getAddress(hepek); // checksum
  const encoded = abi.encode(
    ["bytes32", "address", "uint256", "uint8", "uint256", "uint256", "uint256", "uint256", "bytes32"],
    [
      READING_V2_DOMAIN,
      hepekAddr,
      BigInt(bucketTs),
      Number(dir),
      BigInt(produced ?? 0),
      BigInt(exported ?? 0),
      BigInt(consumed ?? 0),
      BigInt(factor ?? 0),
      prevHash
    ]
  );

  return keccak256(encoded);
}

/**
 * Verify against a readingHash from DB / chain.
 */
export function verifyReadingHashV2(o, expectedHash) {

  const m = o.meter_reading || o.reading || o; // support different input shapes
  const dirCode =
    m.direction === "OUT" ? 1 :
      m.direction === "IN" ? 2 :
        m.direction === "GENESIS" ? 0 : (() => { throw new Error(`Bad direction: ${m.direction}`); })();

  const timestamp = parseAnyTimestampToEpochSeconds(o.timestamp);

  const computed = computeReadingHashV2({
    hepek: m.hepek,
    bucketTs: timestamp,
    dir: dirCode,
    produced: m.produced_power ?? m.produced ?? 0,
    exported: m.exported_power ?? m.exported ?? 0,
    consumed: m.consumed_power ?? m.consumed ?? 0,
    factor: m.FACTOR ?? m.factor ?? 0,
    prevHash: m.previous_reading_hash ?? m.prev
  });

  return computed.toLowerCase() === String(expectedHash).toLowerCase();
}








const PROVENANCE_API_BASE = process.env.REACT_APP_PROVENANCE_API_BASE || "https://serenity.ing";
const HEPEK_PROVENANCE_API_BASE = process.env.REACT_APP_HEPEK_PROVENANCE_API_BASE || "https://serenity.ing/hepek-provenance";
const HEPEK_PROVENANCE_API_KEY = process.env.REACT_APP_HEPEK_PROVENANCE_API_KEY || "";
const HEPEK_PROVENANCE_API_KEY_HEADER = process.env.REACT_APP_HEPEK_PROVENANCE_API_KEY_HEADER || "Authorization";
const READING_API_BASE = process.env.REACT_APP_READING_API_BASE || "https://serenity.ing";

const READING_V2_DOMAIN = keccak256(toUtf8Bytes("READING_V2")); // bytes32
const abi = AbiCoder.defaultAbiCoder();

const SCALE = {
  delta: 1e4,
  meter: 1e5,
  mint: 1e4,
};

const BRIDGE_EMITTER_EVENTS_ABI = [
  "event BridgeInitiatedV2(bytes32 indexed messageId,address indexed user,address indexed device,string symbol,bytes32 symbolHash,uint256 amount,bytes32 v1TxHash,uint256 v1BlockNumber,uint256 nonce,bytes32 payloadHash)",
  "event BridgeInitiatedV3(bytes32 indexed messageId,uint256 srcChainId,address srcEmitter,string dstChain,string dstReceiver,bytes32 payloadHash,bytes32 proofHash)",
  "event BridgeInitiatedLots(bytes32 indexed messageId,bytes32 indexed lotsHash,uint256 lotCount)",
  "event TokensLocked(bytes32 indexed messageId,address indexed user,string symbol,uint256 amount)",
  "event TokensUnlocked(bytes32 indexed messageId,address indexed user,string symbol,uint256 amount,string reason)",
  "event TokensBurned(bytes32 indexed messageId,address indexed user,string symbol,uint256 amount)",
  "event BridgeFinalized(bytes32 indexed messageId,bool success,address indexed user,address indexed token,uint256 amount)",
  "event AutoRefundProcessed(bytes32 indexed messageId,address indexed user,string symbol,uint256 amount,string reason)",
];

const NFT_AUDIT_MINT_EVENTS_ABI = [
  "event Transfer(address indexed from,address indexed to,uint256 indexed tokenId)",
  "event CarbonCreditMinted(address indexed to,uint256 indexed tokenId,uint256 burnedAmount,string uri,address indexed device)",
  "event CarbonCreditMintedV2(address indexed to,uint256 indexed tokenId,uint256 burnedAmount,bytes32 provenanceRoot,bytes32 indexed messageId)",
];

// ---------- Role model ----------
const ROLE_RANK = { user: 1, auditor: 2, admin: 3, public_auditor: 2 };
const roleAtLeast = (role, minRole) => (ROLE_RANK[role] || 0) >= (ROLE_RANK[minRole] || 0);

// Format timestamp (seconds or ms) to readable string
function fmtTs(ts) {
  if (!ts) return "—";
  const t = ts > 1e12 ? new Date(ts) : new Date(ts * 1000);
  return t.toLocaleString();
}

// Safely convert value to number, fallback to 0 if invalid
function safeNum(v) {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function short(str, len = 6) {
  if (!str || typeof str !== "string") return "";
  if (str.length <= len * 2 + 2) return str;
  return str.slice(0, len) + "..." + str.slice(-len);
}

function trimApiBase(base) {
  return String(base || "").replace(/\/+$/, "");
}

function hepekProvenanceHeaders() {
  const headers = { Accept: "application/json" };
  const apiKey = String(HEPEK_PROVENANCE_API_KEY || "").trim();
  if (!apiKey) return headers;

  if (String(HEPEK_PROVENANCE_API_KEY_HEADER || "").toLowerCase() === "x-api-key") {
    headers["X-API-Key"] = apiKey;
  } else {
    headers.Authorization = `Bearer ${apiKey}`;
  }
  return headers;
}

async function fetchJsonGet(base, path) {
  const response = await fetch(`${trimApiBase(base)}${path}`, {
    method: "GET",
    headers: hepekProvenanceHeaders(),
  });

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!response.ok) {
    const message =
      (data && typeof data === "object" && (data.error || data.message)) ||
      `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

async function fetchHepekProvenanceBundle(txHash) {
  const cleanHash = String(txHash || "").trim();
  if (!cleanHash) throw new Error("tx hash missing");

  const encodedHash = encodeURIComponent(cleanHash);
  const endpoints = [
    ["discover", `/discover/tx/${encodedHash}`],
    ["audit", `/audit/tx/${encodedHash}`],
    ["provenance", `/provenance/tx/${encodedHash}/devices`],
  ];

  const settled = await Promise.allSettled(
    endpoints.map(([key, path]) => fetchJsonGet(HEPEK_PROVENANCE_API_BASE, path).then((data) => ({ key, data })))
  );

  const bundle = {
    txHash: cleanHash,
    discover: null,
    audit: null,
    provenance: null,
    errors: [],
  };

  settled.forEach((item, idx) => {
    const key = endpoints[idx][0];
    if (item.status === "fulfilled") {
      bundle[key] = item.value.data;
    } else {
      bundle.errors.push({ key, message: item.reason?.message || String(item.reason) });
    }
  });

  return bundle;
}

function normalizeHepekAuditBundle(bundle) {
  const requestErrors = toArray(bundle?.errors).map((e) => `${e.key}: ${e.message}`);
  const viewModel = buildHepekProvenanceViewModel({
    discover: bundle?.discover,
    audit: bundle?.audit,
    provenance: bundle?.provenance,
    txHash: bundle?.txHash || "",
    requestErrors,
  });

  return {
    ...viewModel,
    tokenId: viewModel.queryTokenIds[0] ?? "",
    hepek: viewModel,
  };
}

function parseAddrList(s) {
  const set = new Set();
  if (!s) return set;
  String(s)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .forEach((x) => {
      try {
        set.add(ethers.getAddress(x).toLowerCase());
      } catch {
        // ignore invalid
      }
    });
  return set;
}

function safeToNumberUnits(v, decimals) {
  try {
    if (v == null) return null;
    return Number(formatUnits(v.toString(), decimals));
  } catch {
    return null;
  }
}

function stableStringifyFlat(obj) {
  const keys = Object.keys(obj).sort();
  const out = {};
  for (const k of keys) out[k] = obj[k];
  return JSON.stringify(out);
}

function parseAnyTimestampToEpochSeconds(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(ts);
  if (ts instanceof Date) return Math.floor(ts.getTime() / 1000);
  if (typeof ts === "string") {
    const s0 = ts.includes("T") ? ts : ts.replace(" ", "T");
    const s =
      s0.endsWith("Z") || /[+-]\d\d:\d\d$/.test(s0)
        ? s0
        : `${s0}Z`; // assume UTC if no zone
    const d = new Date(s);
    const ms = d.getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
  }
  return 0;
}

async function resolveMintTxHashByTokenId({ rpcUrl, nftAddress, tokenId }) {
  if (!rpcUrl || !nftAddress || tokenId == null || tokenId === "") return "";
  if (!ethers.isAddress(nftAddress)) return "";

  let tokenIdBig;
  try {
    tokenIdBig = BigInt(String(tokenId));
  } catch {
    return "";
  }

  const provider = new ethers.JsonRpcProvider(rpcUrl);
  const iface = new ethers.Interface(NFT_AUDIT_MINT_EVENTS_ABI);
  const tokenTopic = ethers.zeroPadValue(ethers.toBeHex(tokenIdBig), 32);
  const zeroAddressTopic = ethers.zeroPadValue(ethers.ZeroAddress, 32);

  const [mintV2Logs, mintV1Logs, transferLogs] = await Promise.all([
    provider.getLogs({
      address: nftAddress,
      topics: [iface.getEvent("CarbonCreditMintedV2").topicHash, null, tokenTopic],
      fromBlock: 0,
      toBlock: "latest",
    }),
    provider.getLogs({
      address: nftAddress,
      topics: [iface.getEvent("CarbonCreditMinted").topicHash, null, tokenTopic],
      fromBlock: 0,
      toBlock: "latest",
    }),
    provider.getLogs({
      address: nftAddress,
      topics: [iface.getEvent("Transfer").topicHash, zeroAddressTopic, null, tokenTopic],
      fromBlock: 0,
      toBlock: "latest",
    }),
  ]);

  const candidates = [...mintV2Logs, ...mintV1Logs, ...transferLogs].filter((log) => Boolean(log?.transactionHash));
  if (!candidates.length) return "";

  candidates.sort((a, b) => {
    const bn = Number(a.blockNumber || 0) - Number(b.blockNumber || 0);
    if (bn !== 0) return bn;
    return Number(a.index ?? a.logIndex ?? 0) - Number(b.index ?? b.logIndex ?? 0);
  });

  return String(candidates[candidates.length - 1].transactionHash || "").trim();
}

// UI: bucket helpers
function bucketByDay(events) {
  const map = new Map();
  for (const e of events) {
    if (!e.timestamp) continue;
    const d = new Date(e.timestamp * 1000);
    const key = d.toISOString().slice(0, 10);
    map.set(key, (map.get(key) || 0) + 1);
  }
  const labels = Array.from(map.keys()).sort();
  const counts = labels.map((k) => map.get(k) || 0);
  return { labels, counts };
}

function bucketBridgeFinalizedByDay(events) {
  const successMap = new Map();
  const failMap = new Map();

  for (const e of events) {
    if (e.source !== "BridgeEmitter") continue;
    if (e.eventName !== "BridgeFinalized") continue;
    if (!e.timestamp) continue;

    const d = new Date(e.timestamp * 1000);
    const key = d.toISOString().slice(0, 10);
    const ok = Boolean(e.args?.success);

    if (ok) successMap.set(key, (successMap.get(key) || 0) + 1);
    else failMap.set(key, (failMap.get(key) || 0) + 1);
  }

  const labels = Array.from(new Set([...successMap.keys(), ...failMap.keys()])).sort();
  const success = labels.map((k) => successMap.get(k) || 0);
  const fail = labels.map((k) => failMap.get(k) || 0);
  return { labels, success, fail };
}

/* --------------------- Helpers / utils --------------------- */

function buildEventMeta() {
  // minRole: who is allowed to even see/select the event type
  // userSelectable: whether event type should appear in user selection UI
  return {
    ErgonEnergy: {
      // user-visible (scoped)
      PowerOutAudit: { minRole: "user", userSelectable: true },
      PowerInAudit: { minRole: "user", userSelectable: true },
      DemandExportBreached: { minRole: "user", userSelectable: true },
      RedeemCarbon: { minRole: "user", userSelectable: true },
      // core event: device error reporting
      DeviceErrorUpdated: { minRole: "user", userSelectable: true },

      // auditor/admin
      OracleUpdated: { minRole: "auditor", userSelectable: false },
      DemandResponseUpdated: { minRole: "auditor", userSelectable: false },
      UserAdded: { minRole: "auditor", userSelectable: false },
      UserUpdated: { minRole: "auditor", userSelectable: false },
      UserGeoUpdated: { minRole: "auditor", userSelectable: false },
      UserStatusUpdated: { minRole: "auditor", userSelectable: false },
      SubmitterApproved: { minRole: "auditor", userSelectable: false },
    },
    Carbon: {
      // user-visible (scoped)
      CarbonMintLotDelta: { minRole: "user", userSelectable: true },
      CarbonLotTransfer: { minRole: "user", userSelectable: true },
      CarbonLotBurn: { minRole: "user", userSelectable: true },
      CarbonLotFrozen: { minRole: "user", userSelectable: true },
      CarbonLotUnfrozen: { minRole: "user", userSelectable: true },
      CarbonMint: { minRole: "user", userSelectable: true },
      CarbonBurn: { minRole: "user", userSelectable: true },

      // auditor/admin only (period anchoring)
      PeriodSecondsUpdated: { minRole: "auditor", userSelectable: false },
      PeriodClosed: { minRole: "auditor", userSelectable: false },
      DevicePeriodCommitted: { minRole: "auditor", userSelectable: false },
      PeriodGlobalCommitted: { minRole: "auditor", userSelectable: false },
    },
    BridgeEmitter: {
      // user-visible (scoped by user or messageId linkage)
      BridgeInitiatedV2: { minRole: "user", userSelectable: true },
      BridgeInitiatedV3: { minRole: "user", userSelectable: true },
      BridgeInitiatedLots: { minRole: "user", userSelectable: true },
      TokensLocked: { minRole: "user", userSelectable: true },
      TokensUnlocked: { minRole: "user", userSelectable: true },
      TokensBurned: { minRole: "user", userSelectable: true },
      BridgeFinalized: { minRole: "user", userSelectable: true },
      AutoRefundProcessed: { minRole: "user", userSelectable: true },
    },
  };
}

function normalizeEvent(ev, source, eventName) {
  return {
    source,
    eventName,
    blockNumber: ev.blockNumber,
    transactionHash: ev.transactionHash,
    args: ev.args || {},
    timestamp: null,
  };
}

function primaryId(ev) {
  const a = ev.args || {};
  return (
    a.readingHash ||
    a.provenanceHash ||
    a.messageId ||
    a.lotsHash ||
    a.payloadHash ||
    a.proofHash ||
    a.sourceHash ||
    a.lotId ||
    a.id ||
    (a.periodId != null ? String(a.periodId) : "") ||
    (a.batchId != null ? String(a.batchId) : "") ||
    ""
  );
}

function stringifyArgs(obj) {
  if (obj == null) return obj;
  if (typeof obj === "bigint") return obj.toString();
  if (Array.isArray(obj)) return obj.map(stringifyArgs);
  if (typeof obj === "object") {
    const out = {};
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (k === "provider" || k === "interface") continue;
      out[k] = stringifyArgs(v);
    }
    return out;
  }
  return obj;
}

function makeSparseLabels(arr, maxLabels = 8) {
  if (!arr.length) return [];
  const step = Math.max(1, Math.floor(arr.length / maxLabels));
  return arr.map((ev, i) => {
    if (i % step !== 0) return "";
    if (ev.timestamp) return new Date(ev.timestamp * 1000).toLocaleDateString();
    return String(ev.blockNumber ?? "");
  });
}

function downloadBlob(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(x) {
  const s = (x ?? "").toString();
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function eventSummary(ev, { scale, safeFmtUnits }) {
  const a = ev.args || {};
  const device = a.device || a.hepekID || a.holder || a.from || a.primaryDevice || "";
  const user = a.user || a.userID || a.to || a.operator || "";

  // Helper to format big numbers as tokens (18 decimals) or kWh/kW
  const fmtToken = (v, symbol = "") => (v != null ? `${safeFmtUnits(v, 18)}${symbol ? " " + symbol : ""}` : "");
  const fmtKWh = (v) => (v != null ? `${scale(v, SCALE.meter).toFixed(3)} kWh` : "");
  const fmtKW = (v) => (v != null ? `${scale(v, SCALE.delta).toFixed(2)} kW` : "");
  const fmtMint = (v, symbol) => (v != null ? `${scale(v, SCALE.mint).toFixed(3)} ${symbol}` : "");

  let amount = "";
  if (a.amount != null) {
    if (ev.source === "Carbon" || ev.source === "BridgeEmitter") amount = fmtToken(a.amount);
    else if (ev.source === "ErgonEnergy") amount = fmtKWh(a.amount);
    else amount = String(a.amount);
  } else if (a.delta != null) {
    amount = fmtKW(a.delta);
  } else if (a.ergonMint != null) {
    amount = fmtMint(a.ergonMint, "ERGON");
  } else if (a.carbonMint != null) {
    amount = fmtMint(a.carbonMint, "CARBON");
  } else if (a.deltaAmount != null) {
    amount = fmtToken(a.deltaAmount, "CARBON");
  } else if (a.totalLotAmount != null) {
    amount = fmtToken(a.totalLotAmount, "CARBON");
  }

  let summary = "";
  switch (ev.eventName) {
    case "PowerOutAudit": {
      const ex = scale(a._export, SCALE.meter);
      const im = scale(a._import, SCALE.meter);
      const produced = ex - im;
      summary = `produced=${produced.toFixed(3)}kWh export=${ex.toFixed(3)} import=${im.toFixed(3)} ergonMint=${scale(a.ergonMint, SCALE.mint).toFixed(3)} carbonMint=${scale(a.carbonMint, SCALE.mint).toFixed(3)}`;
      break;
    }
    case "DemandExportBreached":
      summary = `export=${a.exportPower} limit=${a.exportLimit}`;
      break;
    case "OracleUpdated":
      summary = `ergonPrice=${safeFmtUnits(a.ergonPrice, 18)} carbonPrice=${safeFmtUnits(a.carbonPrice, 18)}`;
      break;
    case "CarbonMintLotDelta":
      summary = `lotDelta=${fmtToken(a.deltaAmount, "CARBON")} totalLot=${fmtToken(a.totalLotAmount, "CARBON")} periodId=${a.batchId}`;
      break;
    case "BridgeInitiatedLots":
      summary = `lotsHash=${a.lotsHash} lotCount=${a.lotCount}`;
      break;
    case "BridgeFinalized":
      summary = `success=${a.success} token=${a.token} amount=${fmtToken(a.amount)}`;
      break;
    case "TokensUnlocked":
      summary = `reason=${a.reason || ""}`;
      break;
    default:
      break;
  }

  return { device, user, amount, summary };
}

function lineageColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "complete_mint_lineage") return "success";
  if (s === "consumed_but_mint_missing") return "warning";
  if (s === "unresolved") return "error";
  return "default";
}

function checkColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pass" || s === "passed" || s === "ok" || s === "true") return "success";
  if (s === "fail" || s === "failed" || s === "false") return "error";
  if (s === "warn" || s === "warning") return "warning";
  return "default";
}

function JsonPreview({ data, maxHeight = 180 }) {
  if (data == null) return null;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        maxHeight,
        overflow: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        fontFamily: "monospace",
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
    </Box>
  );
}

function HepekAuditPanel({ result, compact = false }) {
  const vm = result?.hepek || result || {};
  const metadata = vm.metadata || {};
  const warnings = toArray(vm.warnings);
  const warningCards = toArray(vm.warningCards);
  const limitations = toArray(vm.limitations);
  const checks = toArray(vm.checks);
  const lifecycle = toArray(vm.nftLifecycle);
  const bridgedLots = toArray(vm.bridgedLots);
  const devices = toArray(vm.contributingDevices);
  const relatedTokenIds = toArray(vm.relatedTokenIds);
  const queryTokenIds = toArray(vm.queryTokenIds);
  const requestErrors = toArray(vm.requestErrors);
  const canonical = vm.canonical;

  return (
    <Box sx={{ mt: compact ? 1 : 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        <Chip size="small" color="info" variant="outlined" label="HEPEK V2" />
        <Chip size="small" variant="outlined" label={`network: ${metadata.network || "-"}`} />
        <Chip size="small" variant="outlined" label={`chain: ${metadata.chain || "-"}`} />
        <Chip size="small" variant="outlined" label={`type: ${metadata.type || "-"}`} />
        <Chip size="small" variant="outlined" label={`event kinds: ${metadata.eventKinds || "-"}`} />
        <Chip
          size="small"
          color={warnings.length ? "warning" : "success"}
          variant="outlined"
          label={warnings.length ? `${warnings.length} warning${warnings.length === 1 ? "" : "s"}` : "clean"}
        />
      </Box>

      {vm.summary && (
        <Alert severity={vm.verdict === "FAIL" ? "error" : vm.verdict === "WARN" ? "warning" : "info"} sx={{ mb: 1 }}>
          {typeof vm.summary === "string" ? vm.summary : JSON.stringify(vm.summary)}
        </Alert>
      )}

      {!!checks.length && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.25 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Check</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.map((check, idx) => (
                <TableRow key={`${check.name}-${idx}`}>
                  <TableCell sx={{ width: "40%", whiteSpace: "normal", wordBreak: "break-word" }}>
                    {check.name}
                  </TableCell>
                  <TableCell sx={{ width: "14%" }}>
                    <Chip size="small" color={checkColor(check.status)} variant="outlined" label={check.status} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {check.details || ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(queryTokenIds.length || relatedTokenIds.length) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
          {queryTokenIds.map((id) => <Chip key={`query-${id}`} size="small" color="primary" variant="outlined" label={`Query token #${id}`} />)}
          {relatedTokenIds.map((id) => <Chip key={`related-${id}`} size="small" variant="outlined" label={`Related token #${id}`} />)}
        </Box>
      )}

      {!!lifecycle.length && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>NFT Lifecycle</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {lifecycle.map((item) => (
              <Box key={`life-${item.tokenId}-${item.relationToQuery}`} sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" label={`NFT #${item.tokenId}`} variant="outlined" />
                  <Chip size="small" label={item.relationToQuery || "unrelated"} variant="outlined" />
                  {item.retired === true && <Chip size="small" label="retired" color="default" />}
                  {item.marketplaceEventCount > 0 && <Chip size="small" label="marketplace history" color="info" variant="outlined" />}
                </Box>
                <Grid container spacing={1}>
                  {[
                    ["retiredBy", item.retiredBy],
                    ["currentOwner", item.currentOwner],
                    ["mintEventCount", item.mintEventCount],
                    ["transferEventCount", item.transferEventCount],
                    ["marketplaceEventCount", item.marketplaceEventCount],
                    ["retirementEventCount", item.retirementEventCount],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${item.tokenId}-${label}`}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{String(value)}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!!devices.length && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Contributing Devices</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {devices.map((device) => (
              <Box key={device.deviceId} sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" color={lineageColor(device.lineageStatus)} variant="outlined" label={device.lineageStatus} />
                  <Chip size="small" variant="outlined" label={short(device.deviceId, 10)} />
                </Box>
                <Grid container spacing={1}>
                  {[
                    ["deviceId", device.deviceId],
                    ["mintedAmount", device.mintedAmount],
                    ["mintedEventCount", device.mintedEventCount],
                    ["bridgeConsumedAmount", device.bridgeConsumedAmount],
                    ["bridgeConsumedEventCount", device.bridgeConsumedEventCount],
                    ["firstMintBlockNumber", device.firstMintBlockNumber],
                    ["lastMintBlockNumber", device.lastMintBlockNumber],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${device.deviceId}-${label}`}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{String(value)}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!!bridgedLots.length && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Bridged Lots</Typography>
          {bridgedLots.map((lot) => (
              <Box key={lot.lotId} sx={{ mb: 0.75, p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 0.5 }}>
                  <Chip size="small" color={lineageColor(lot.lineageStatus)} variant="outlined" label={lot.lineageStatus} />
                  <Chip size="small" variant="outlined" label={short(lot.lotId, 8)} />
                </Box>
                <Grid container spacing={1}>
                  {[
                    ["lotId", lot.lotId],
                    ["sourceMintedAmount", lot.sourceMintedAmount],
                    ["sourceConsumedAmount", lot.sourceConsumedAmount],
                    ["mintEventCount", lot.mintEventCount],
                    ["consumptionEventCount", lot.consumptionEventCount],
                    ["sourceDevices", lot.sourceDevices || "-"],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${lot.lotId}-${label}`}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{String(value)}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
          ))}
        </Box>
      )}

      {!!warningCards.length && (
        <Box sx={{ mb: 1 }}>
          {warningCards.map((warning, idx) => {
            const detailItems = toArray(warning.lotsMissingMintLineage);
            return (
              <Alert key={`${warning.message}-${idx}`} severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2">{warning.message}</Typography>
                {!!detailItems.length && (
                  <Box component="details" sx={{ mt: 1 }}>
                    <Box component="summary" sx={{ cursor: "pointer", fontWeight: 700 }}>
                      lotsMissingMintLineage details ({detailItems.length})
                    </Box>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                      {detailItems.map((item, itemIdx) => (
                        <li key={`missing-lineage-${itemIdx}`}>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                            {String(item?.lotId || item?.messageId || item?.id || JSON.stringify(item))}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
              </Alert>
            );
          })}
        </Box>
      )}

      {!warnings.length && (
        <Alert severity="success" sx={{ mb: 1 }}>warnings empty / clean</Alert>
      )}

      {!!requestErrors.length && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          request caveats: {requestErrors.map((w) => String(w)).join(" | ")}
        </Alert>
      )}

      {!!limitations.length && (
        <Alert severity="info" sx={{ mb: 1 }}>
          limitations: {limitations.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(" | ")}
        </Alert>
      )}

      {canonical && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Canonical Records / Digest</Typography>
          <JsonPreview data={canonical} maxHeight={compact ? 140 : 220} />
        </Box>
      )}
    </Box>
  );
}

/* UI helpers */
function KpiCard({ title, value, icon }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon}
          <Box>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h6">{value}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <Box sx={{ p: 1, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: color || "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}

function ProvenanceHome() {
  const location = useLocation();
  // Device filter state
  const [selectedDevice, setSelectedDevice] = useState("");
  const [actionDescription, setActionDescription] = React.useState("");
  const [actionTitle, setActionTitle] = React.useState("");

  // Ensure viewerAddress is set from wallet
  useEffect(() => {
    async function fetchWalletAddress() {
      if (window.ethereum && window.ethereum.request) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0) {
            setViewerAddress(accounts[0]);
          }
        } catch { }
      }
    }
    fetchWalletAddress();
    // Listen for account changes
    if (window.ethereum && window.ethereum.on) {
      const handler = (accounts) => {
        if (accounts && accounts.length > 0) setViewerAddress(accounts[0]);
        else setViewerAddress("");
      };
      window.ethereum.on('accountsChanged', handler);
      return () => window.ethereum.removeListener('accountsChanged', handler);
    }
  }, []);

  // When user clicks Connect wallet, also set viewerAddress
  const handleConnectWallet = async () => {
    if (window.ethereum && window.ethereum.request) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts && accounts.length > 0) {
          setViewerAddress(accounts[0]);
        }
      } catch { }
    }
    if (typeof refreshWallet === 'function') refreshWallet(true);
  };


  // On mount, try to get wallet address if available
  useEffect(() => {
    async function fetchWalletAddress() {
      if (window.ethereum && window.ethereum.selectedAddress) {
        setViewerAddress(window.ethereum.selectedAddress);
      } else if (window.ethereum && window.ethereum.request) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts && accounts.length > 0) {
            setViewerAddress(accounts[0]);
          }
        } catch { }
      }
    }
    fetchWalletAddress();
  }, []);
  const theme = useTheme();

  // ENV (stable)
  const SRC_RPC = process.env.REACT_APP_SOURCE_RPC_URL;
  const SRC_WS = process.env.REACT_APP_SOURCE_WS_URL;
  const SRC_NET_ID = process.env.REACT_APP_SOURCE_NETWORK_ID;

  const ENERGY_ADDR = process.env.REACT_APP_SOURCE_ERGONENERGY_CONTRACT_ADDRESS;
  const CARBON_ADDR = process.env.REACT_APP_SOURCE_CARBON_CONTRACT_ADDRESS;
  const BRIDGE_ADDR = process.env.REACT_APP_SOURCE_BRIDGEEMITTER_CONTRACT_ADDRESS;
  const BRIDGE_RECEIVER_ADDR =
    process.env.REACT_APP_DEST_BRIDGE_RECEIVER_CONTRACT_ADDRESS ||
    process.env.REACT_APP_DEST_BRIDGE_RECEIVER_ADDRESS ||
    process.env.REACT_APP_BRIDGE_RECEIVER_CONTRACT_ADDRESS ||
    process.env.REACT_APP_BRIDGE_RECEIVER_ADDRESS ||
    "";
  const NFT_AUDIT_RPC = process.env.REACT_APP_NFT_AUDIT_RPC_URL || SRC_RPC || "https://serenity.ing/cpr";
  const NFT_AUDIT_VAULT =
    process.env.REACT_APP_NFT_AUDIT_VAULT_ADDRESS ||
    process.env.REACT_APP_PLATFORMVAULT_ADDRESS ||
    process.env.REACT_APP_VAULT_CONTRACT_ADDRESS ||
    "";
  const NFT_AUDIT_NFT =
    process.env.REACT_APP_NFT_AUDIT_NFT_ADDRESS ||
    process.env.REACT_APP_CARBOCREDIT_NFT_ADDRESS ||
    process.env.REACT_APP_NFT_CONTRACT_ADDRESS ||
    "";
  const NFT_AUDIT_ETHCARBON =
    process.env.REACT_APP_NFT_AUDIT_ETHCARBON_ADDRESS ||
    process.env.REACT_APP_CARBON_CONTRACT_ADDRESS ||
    process.env.REACT_APP_SOURCE_CARBON_CONTRACT_ADDRESS ||
    process.env.REACT_APP_ETH_CARBON_CONTRACT_ADDRESS ||
    "";

  const ALLOW_PUBLIC_AUDITOR = String(process.env.REACT_APP_PROVENANCE_ALLOW_PUBLIC_AUDITOR || "").toLowerCase() === "true";
  const ADMIN_ADDRS = useMemo(() => parseAddrList(process.env.REACT_APP_PROVENANCE_ADMIN_ADDRESSES), []);
  const AUDITOR_ADDRS = useMemo(() => parseAddrList(process.env.REACT_APP_PROVENANCE_AUDITOR_ADDRESSES), []);

  const hasProviderConfig = Boolean(SRC_WS || SRC_RPC) && Boolean(ENERGY_ADDR);

  // Tabs (0=Events,1=Audit,2=Diagnostics,3=NFT Audit,4=Geolocations)
  const [tab, setTab] = useState(0);

  // Wallet / Role
  const [viewerAddress, setViewerAddress] = useState();
  const [viewerRole, setViewerRole] = useState(ALLOW_PUBLIC_AUDITOR ? "public_auditor" : "user");
  const [canSwitchRole, setCanSwitchRole] = useState(false);

  // Provider
  const [provider, setProvider] = useState(null);
  const [providerKind, setProviderKind] = useState("none"); // ws|rpc|none

  // Fetch/log state
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(0);

  const [queryFailures, setQueryFailures] = useState([]);

  const [log, setLog] = useState([]);
  const logRef = useRef(null);

  const addLog = useCallback((msg) => {
    setLog((prev) => {
      const line = `${new Date().toLocaleString()} | ${msg}`;
      const next = [line, ...prev];
      return next.slice(0, 250);
    });
  }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = 0;
  }, [log]);

  // Raw events (as fetched)
  const [rawEvents, setRawEvents] = useState([]);

  // UI state
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Sorting/pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("blockNumber"); // blockNumber|timestamp|eventName|source

  // Time range + blocks
  const [timeRange, setTimeRange] = useState("day");
  const [autoFromBlock, setAutoFromBlock] = useState(true);
  const [fromBlock, setFromBlock] = useState("");
  const [toBlock, setToBlock] = useState("latest");
  const [maxEvents, setMaxEvents] = useState(2500);

  // Snackbars
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  // NFT audit tab
  const [nftAuditForm, setNftAuditForm] = useState({
    rpcUrl: NFT_AUDIT_RPC,
    txHash: "",
    tokenId: "",
    vaultAddress: NFT_AUDIT_VAULT,
    nftAddress: NFT_AUDIT_NFT,
    ethCarbonAddress: NFT_AUDIT_ETHCARBON,
    fromBlock: "0",
  });
  const [nftAuditLoading, setNftAuditLoading] = useState(false);
  const [nftAuditV2Loading, setNftAuditV2Loading] = useState(false);
  const [nftAuditResult, setNftAuditResult] = useState(null);

  useEffect(() => {
    setNftAuditForm((prev) => ({
      ...prev,
      rpcUrl: prev.rpcUrl || NFT_AUDIT_RPC,
      vaultAddress: prev.vaultAddress || NFT_AUDIT_VAULT,
      nftAddress: prev.nftAddress || NFT_AUDIT_NFT,
      ethCarbonAddress: prev.ethCarbonAddress || NFT_AUDIT_ETHCARBON,
    }));
  }, [NFT_AUDIT_RPC, NFT_AUDIT_VAULT, NFT_AUDIT_NFT, NFT_AUDIT_ETHCARBON]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const tabParam = String(params.get("tab") || "").toLowerCase();
    const txHashParam = String(params.get("txHash") || params.get("tx") || "").trim();
    const tokenIdParam = String(params.get("tokenId") || params.get("nftId") || "").trim();

    const shouldOpenNftAuditTab = tabParam === "nft-audit" || tabParam === "nftaudit" || tabParam === "nft";
    const hasPrefill = Boolean(txHashParam || tokenIdParam);

    if (!shouldOpenNftAuditTab && !hasPrefill) return;

    setTab(3);
    setNftAuditForm((prev) => {
      const next = { ...prev };
      if (txHashParam) next.txHash = txHashParam;
      if (tokenIdParam) next.tokenId = tokenIdParam;
      return next;
    });

    if (hasPrefill) {
      setSnack({ open: true, msg: "NFT audit prefilled from selected retirement entry", severity: "info" });
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    async function swapRetirementTxHashForMintTxHash() {
      const params = new URLSearchParams(location.search || "");
      const txHashParam = String(params.get("txHash") || params.get("tx") || "").trim();
      const tokenIdParam = String(params.get("tokenId") || params.get("nftId") || "").trim();

      if (!txHashParam || !tokenIdParam) return;

      const rpcUrl = (nftAuditForm.rpcUrl || NFT_AUDIT_RPC || "").trim();
      const nftAddress = (nftAuditForm.nftAddress || NFT_AUDIT_NFT || "").trim();
      if (!rpcUrl || !nftAddress) return;

      try {
        const mintTxHash = await resolveMintTxHashByTokenId({
          rpcUrl,
          nftAddress,
          tokenId: tokenIdParam,
        });

        if (!mintTxHash) return;
        if (mintTxHash.toLowerCase() === txHashParam.toLowerCase()) return;
        if (cancelled) return;

        setNftAuditForm((prev) => {
          const currentTxHash = String(prev.txHash || "").trim().toLowerCase();
          if (currentTxHash === mintTxHash.toLowerCase()) return prev;
          return {
            ...prev,
            txHash: mintTxHash,
            tokenId: prev.tokenId || tokenIdParam,
          };
        });

        setSnack({ open: true, msg: "Mint tx hash auto-selected for NFT audit", severity: "info" });
      } catch {
        // Keep original tx hash if mint hash lookup is unavailable.
      }
    }

    swapRetirementTxHashForMintTxHash();
    return () => {
      cancelled = true;
    };
  }, [location.search, nftAuditForm.rpcUrl, nftAuditForm.nftAddress, NFT_AUDIT_RPC, NFT_AUDIT_NFT]);

  useEffect(() => {
    let cancelled = false;

    async function resolveNftAuditCarbonAddress() {
      const receiverAddress = (BRIDGE_RECEIVER_ADDR || "").trim();
      const rpcUrl = (nftAuditForm.rpcUrl || NFT_AUDIT_RPC || "").trim();
      const configuredEthCarbon = (NFT_AUDIT_ETHCARBON || "").trim();

      if (!receiverAddress || !rpcUrl) return;

      try {
        const provider = new ethers.JsonRpcProvider(rpcUrl);
        const receiver = new ethers.Contract(
          receiverAddress,
          [
            "function symbolKey(string symbol) view returns (bytes32)",
            "function tokenByKey(bytes32 key) view returns (address)"
          ],
          provider
        );

        const key = await receiver.symbolKey("CARBON");
        const mapped = await receiver.tokenByKey(key);
        if (!mapped || mapped === ethers.ZeroAddress) return;

        const mappedAddress = ethers.getAddress(mapped);

        if (cancelled) return;
        setNftAuditForm((prev) => {
          const current = (prev.ethCarbonAddress || "").trim();
          const currentLower = current.toLowerCase();
          const mappedLower = mappedAddress.toLowerCase();
          const configuredLower = configuredEthCarbon.toLowerCase();

          if (currentLower === mappedLower) return prev;

          const canAutoReplace = !current || !configuredEthCarbon || currentLower === configuredLower;
          if (!canAutoReplace) return prev;

          if (configuredEthCarbon && configuredLower !== mappedLower) {
            addLog(`NFT audit token config drift detected: configured=${configuredEthCarbon}, receiverMapped=${mappedAddress}`);
          }

          return { ...prev, ethCarbonAddress: mappedAddress };
        });
      } catch {
        // Keep manual/env value if receiver lookup is unavailable.
      }
    }

    resolveNftAuditCarbonAddress();
    return () => {
      cancelled = true;
    };
  }, [BRIDGE_RECEIVER_ADDR, NFT_AUDIT_ETHCARBON, NFT_AUDIT_RPC, addLog, nftAuditForm.rpcUrl]);

  // Detail dialog
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [txAuditResult, setTxAuditResult] = useState(null);
  const [txAuditLoading, setTxAuditLoading] = useState(false);
  const [txAuditError, setTxAuditError] = useState(null);

  // Reading verification state for dialog
  const [readingData, setReadingData] = useState(null);
  const [readingStatus, setReadingStatus] = useState(null); // 'ok', 'fail', or null
  const [loadingReading, setLoadingReading] = useState(false);
  const [readingError, setReadingError] = useState(null);
  // Tx sender verification state (tx.from vs expected device)
  const [txSenderStatus, setTxSenderStatus] = useState(null); // 'ok' | 'fail' | 'error' | null
  const [txSenderLoading, setTxSenderLoading] = useState(false);
  const [txSenderInfo, setTxSenderInfo] = useState({ from: "", expected: "" });
  const [txSenderError, setTxSenderError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Reset reading verification state when dialog closes or event changes
    setReadingData(null);
    setReadingStatus(null);
    setLoadingReading(false);
    setReadingError(null);

    setTxSenderStatus(null);
    setTxSenderLoading(false);
    setTxSenderInfo({ from: "", expected: "" });
    setTxSenderError(null);

    setTxAuditResult(null);
    setTxAuditError(null);
    setTxAuditLoading(false);

    const txHash = String(selectedEvent?.transactionHash || "").trim();
    if (detailOpen && txHash) {
      setTxAuditLoading(true);
      fetchHepekProvenanceBundle(txHash)
        .then((bundle) => {
          if (cancelled) return;
          const normalized = normalizeHepekAuditBundle(bundle);
          setTxAuditResult(normalized);
          if (!normalized.ok) {
            setTxAuditError("HEPEK provenance endpoints returned no transaction audit data.");
          } else if (bundle.errors?.length) {
            setTxAuditError(bundle.errors.map((e) => `${e.key}: ${e.message}`).join(" | "));
          }
        })
        .catch((e) => {
          if (!cancelled) setTxAuditError(e.message);
        })
        .finally(() => {
          if (!cancelled) setTxAuditLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [detailOpen, selectedEvent]);

  // Analytics mode (eventName string)
  const [analyticsMode, setAnalyticsMode] = useState("");

  // ---------- Catalog + role gating ----------
  const eventMeta = useMemo(() => buildEventMeta(), []);

  const catalog = useMemo(() => {
    const items = [];
    if (ENERGY_ADDR) {
      items.push({
        source: "ErgonEnergy",
        address: ENERGY_ADDR,
        abi: energyAbi.abi,
        events: Object.keys(eventMeta.ErgonEnergy),
      });
    }
    if (CARBON_ADDR) {
      items.push({
        source: "Carbon",
        address: CARBON_ADDR,
        abi: carbonAbi.abi,
        events: Object.keys(eventMeta.Carbon),
      });
    }
    if (BRIDGE_ADDR) {
      items.push({
        source: "BridgeEmitter",
        address: BRIDGE_ADDR,
        abi: BRIDGE_EMITTER_EVENTS_ABI,
        events: Object.keys(eventMeta.BridgeEmitter),
      });
    }
    return items;
  }, [ENERGY_ADDR, CARBON_ADDR, BRIDGE_ADDR, eventMeta]);

  const selectableCatalog = useMemo(() => {
    return catalog
      .map((c) => ({
        ...c,
        events: c.events.filter((ev) => {
          const meta = eventMeta?.[c.source]?.[ev];
          if (!meta) return false;
          if (!roleAtLeast(viewerRole, meta.minRole)) return false;
          if (viewerRole === "user" && meta.userSelectable !== true) return false;
          return true;
        }),
      }))
      .filter((c) => c.events.length > 0);
  }, [catalog, eventMeta, viewerRole]);

  const selectableOptions = useMemo(() => {
    const out = [];
    selectableCatalog.forEach((c) => c.events.forEach((ev) => out.push({ key: `${c.source}:${ev}`, source: c.source, eventName: ev })));
    return out;
  }, [selectableCatalog]);

  const validSelectableKeys = useMemo(() => new Set(selectableOptions.map((o) => o.key)), [selectableOptions]);

  const PRESETS = useMemo(() => {
    const core = [
      "ErgonEnergy:PowerOutAudit",
      "ErgonEnergy:PowerInAudit",
      "ErgonEnergy:DemandExportBreached",
      "ErgonEnergy:RedeemCarbon",
      "ErgonEnergy:DeviceErrorUpdated"
    ].filter((k) => validSelectableKeys.has(k));
    const lots = [
      "Carbon:PeriodSecondsUpdated",
      "Carbon:CarbonMintLotDelta",
      "Carbon:CarbonLotTransfer",
      "Carbon:CarbonLotBurn",
      "Carbon:CarbonLotFrozen",
      "Carbon:CarbonLotUnfrozen",
      "Carbon:CarbonMint",
      "Carbon:CarbonBurn",
    ].filter((k) => validSelectableKeys.has(k));
    const bridge = [
      "BridgeEmitter:BridgeInitiatedV2",
      "BridgeEmitter:BridgeInitiatedV3",
      "BridgeEmitter:BridgeInitiatedLots",
      "BridgeEmitter:TokensLocked",
      "BridgeEmitter:TokensUnlocked",
      "BridgeEmitter:TokensBurned",
      "BridgeEmitter:BridgeFinalized",
      "BridgeEmitter:AutoRefundProcessed",
    ].filter((k) => validSelectableKeys.has(k));
    const policy = [
      "ErgonEnergy:OracleUpdated",
      "ErgonEnergy:DemandResponseUpdated",
      "ErgonEnergy:UserAdded",
      "ErgonEnergy:UserUpdated",
      "ErgonEnergy:UserStatusUpdated",
      "ErgonEnergy:SubmitterApproved",
      "Carbon:PeriodClosed",
      "Carbon:PeriodSecondsUpdated",
      "Carbon:DevicePeriodCommitted",
      "Carbon:PeriodGlobalCommitted",
    ].filter((k) => validSelectableKeys.has(k));
    return { core, lots, bridge, policy };
  }, [validSelectableKeys]);

  // Selected events
  const [selectedEventKeys, setSelectedEventKeys] = useState([]);

  const defaultSelection = useMemo(() => {
    const base = [
      "ErgonEnergy:PowerOutAudit",
      "ErgonEnergy:PowerInAudit",
      "Carbon:CarbonMintLotDelta",
      "BridgeEmitter:BridgeFinalized",
      "BridgeEmitter:TokensLocked",
      "BridgeEmitter:TokensUnlocked",
    ].filter((k) => validSelectableKeys.has(k));

    if (viewerRole !== "user") {
      base.push(...["ErgonEnergy:OracleUpdated", "Carbon:DevicePeriodCommitted", "Carbon:PeriodGlobalCommitted"].filter((k) => validSelectableKeys.has(k)));
    }
    return Array.from(new Set(base));
  }, [validSelectableKeys, viewerRole]);

  useEffect(() => {
    if (!selectableOptions.length) return;
    setSelectedEventKeys((prev) => {
      const cleaned = (prev || []).filter((k) => validSelectableKeys.has(k));
      if (cleaned.length) return cleaned;
      return defaultSelection.length ? defaultSelection : [selectableOptions[0].key];
    });
  }, [selectableOptions, validSelectableKeys, defaultSelection]);

  const applyPreset = useCallback(
    (which) => {
      if (which === "core") return setSelectedEventKeys(PRESETS.core);
      if (which === "lots") return setSelectedEventKeys(PRESETS.lots);
      if (which === "bridge") return setSelectedEventKeys(PRESETS.bridge);
      if (which === "policy") return setSelectedEventKeys(PRESETS.policy);
      if (which === "all") return setSelectedEventKeys(selectableOptions.map((o) => o.key));
      if (which === "clear") return setSelectedEventKeys([]);
      return undefined;
    },
    [PRESETS, selectableOptions]
  );

  // ---------- wallet / role detection ----------
  const resolveRoleFromWallet = useCallback(
    (addr) => {
      if (!addr) return ALLOW_PUBLIC_AUDITOR ? "public_auditor" : "user";
      const a = addr.toLowerCase();
      if (ADMIN_ADDRS.has(a)) return "admin";
      if (AUDITOR_ADDRS.has(a)) return "auditor";
      return "user";
    },
    [ADMIN_ADDRS, AUDITOR_ADDRS, ALLOW_PUBLIC_AUDITOR]
  );

  const refreshWallet = useCallback(
    async (request = false) => {
      try {
        if (!window.ethereum?.request) {
          setViewerAddress("");
          setViewerRole(ALLOW_PUBLIC_AUDITOR ? "public_auditor" : "user");
          setCanSwitchRole(false);
          return;
        }
        const method = request ? "eth_requestAccounts" : "eth_accounts";
        const accounts = await window.ethereum.request({ method });
        const addr = accounts && accounts[0] ? ethers.getAddress(accounts[0]) : "";
        setViewerAddress(addr || "");
        const role = resolveRoleFromWallet(addr || "");
        setViewerRole(role);
        setCanSwitchRole(role === "admin" || role === "auditor");
      } catch {
        setViewerAddress("");
        setViewerRole(ALLOW_PUBLIC_AUDITOR ? "public_auditor" : "user");
        setCanSwitchRole(false);
      }
    },
    [ALLOW_PUBLIC_AUDITOR, resolveRoleFromWallet]
  );

  useEffect(() => {
    refreshWallet(false);
    if (!window.ethereum?.on) return undefined;
    const handler = () => refreshWallet(false);
    window.ethereum.on("accountsChanged", handler);
    return () => {
      try {
        window.ethereum.removeListener("accountsChanged", handler);
      } catch {
        // ignore
      }
    };
  }, [refreshWallet]);

  // Utility: scale and safeFmtUnits
  const scale = useCallback((v, denom) => {
    const n = safeNum(v);
    if (!n) return 0;
    return n / denom;
  }, []);

  const safeFmtUnits = useCallback((v, decimals = 18) => {
    try {
      if (v == null) return "";
      return formatUnits(v.toString(), decimals);
    } catch {
      return "";
    }
  }, []);

  // Utility: copyToClipboard
  const copyToClipboard = useCallback(async (label, value) => {
    try {
      if (!value) throw new Error("Empty");
      const text = String(value);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setSnack({ open: true, msg: `${label} copied`, severity: "success" });
    } catch {
      setSnack({ open: true, msg: `Failed to copy ${label}`, severity: "error" });
    }
  }, []);

  // ---------- Provider ----------
  const ensureProvider = useCallback(async () => {
    if (!hasProviderConfig) return null;
    if (provider) return provider;

    addLog("Initializing provider…");

    let prov;
    if (SRC_WS) {
      prov = new ethers.WebSocketProvider(SRC_WS);
      setProviderKind("ws");
      addLog(`Connected WS: ${SRC_WS}`);
    } else {
      prov = new ethers.JsonRpcProvider(SRC_RPC);
      setProviderKind("rpc");
      addLog(`Connected RPC: ${SRC_RPC}`);
    }

    try {
      const net = await prov.getNetwork();
      addLog(`Network chainId=${net.chainId.toString()} ${SRC_NET_ID ? `(expected ${SRC_NET_ID})` : ""}`);
    } catch (e) {
      addLog(`Network check skipped: ${e.message}`);
    }

    setProvider(prov);
    return prov;
  }, [SRC_NET_ID, SRC_RPC, SRC_WS, addLog, hasProviderConfig, provider]);

  // Auto fromBlock calculation
  const rangeSeconds = useMemo(() => {
    const map = { hour: 3600, day: 86400, week: 7 * 86400, month: 30 * 86400, "3months": 90 * 86400, year: 365 * 86400, all: 0 };
    return map[timeRange] ?? 0;
  }, [timeRange]);

  const estimateAvgBlockTime = useCallback(async (prov) => {
    const latest = await prov.getBlockNumber();
    const sample = 200;
    const older = Math.max(0, latest - sample);
    const [bLatest, bOld] = await Promise.all([prov.getBlock(latest), prov.getBlock(older)]);
    const dt = Math.max(1, (bLatest?.timestamp ?? 0) - (bOld?.timestamp ?? 0));
    const db = Math.max(1, latest - older);
    return dt / db;
  }, []);

  const computeAutoFromBlock = useCallback(async (prov) => {
    if (!rangeSeconds || rangeSeconds <= 0) return 0;
    const latest = await prov.getBlockNumber();
    const avg = await estimateAvgBlockTime(prov);
    const blocksBack = Math.ceil(rangeSeconds / Math.max(1, avg));
    return Math.max(0, latest - blocksBack);
  }, [estimateAvgBlockTime, rangeSeconds]);

  // ---------- Sync (fetch) ----------
  const handleSync = useCallback(async () => {
    setSearching(true);
    setProgress(0);
    setLog([]);
    setQueryFailures([]);

    try {
      const prov = await ensureProvider();
      if (!prov) {
        addLog("Missing provider config.");
        setSearching(false);
        return;
      }

      setProgress(8);

      let fb = 0;
      const tb = (toBlock || "latest") === "latest" ? "latest" : Number(toBlock);

      if (autoFromBlock) {
        addLog(`Auto-calculating fromBlock for range: ${timeRange}…`);
        fb = await computeAutoFromBlock(prov);
        setFromBlock(String(fb));
        addLog(`Auto fromBlock=${fb}`);
      } else {
        fb = fromBlock === "" ? 0 : Number(fromBlock);
        addLog(`Manual fromBlock=${fb}`);
      }

      setProgress(18);

      if (!selectedEventKeys.length) {
        addLog("No events selected.");
        setRawEvents([]);
        setSearching(false);
        return;
      }

      // map selected keys by source
      const selectedBySource = new Map();
      selectedEventKeys.forEach((k) => {
        const [src, ev] = k.split(":");
        if (!selectedBySource.has(src)) selectedBySource.set(src, new Set());
        selectedBySource.get(src).add(ev);
      });

      const contracts = selectableCatalog
        .map((c) => ({ ...c, wanted: selectedBySource.get(c.source) }))
        .filter((c) => c.wanted && c.wanted.size > 0);

      addLog(`Fetching: ${contracts.map((c) => `${c.source}(${c.wanted.size})`).join(", ")}`);
      setProgress(28);

      const all = [];
      const totalQueries = contracts.reduce((acc, c) => acc + c.wanted.size, 0);
      let doneQueries = 0;

      for (const c of contracts) {
        const contract = new ethers.Contract(c.address, c.abi, prov);
        for (const evName of Array.from(c.wanted)) {
          try {
            const res = await contract.queryFilter(evName, fb, tb);
            res.forEach((ev) => all.push(normalizeEvent(ev, c.source, evName)));
            addLog(`${c.source}.${evName}: ${res.length}`);
          } catch (e) {
            addLog(`⚠️ ${c.source}.${evName} queryFilter failed: ${e.message}`);
            setQueryFailures((prev) => [...prev, `${c.source}.${evName}: ${e.message}`].slice(0, 50));
          } finally {
            doneQueries += 1;
            setProgress(Math.min(60, 28 + Math.round((doneQueries / Math.max(1, totalQueries)) * 32)));
          }
        }
      }

      setProgress(62);

      let flat = all;
      if (flat.length > maxEvents) {
        addLog(`Limiting to latest ${maxEvents} events (was ${flat.length}).`);
        flat = flat
          .slice()
          .sort((a, b) => (b.blockNumber || 0) - (a.blockNumber || 0))
          .slice(0, maxEvents);
      }

      addLog(`Resolving timestamps for ${new Set(flat.map((e) => e.blockNumber)).size} blocks…`);
      setProgress(70);

      const uniqBlocks = Array.from(new Set(flat.map((e) => e.blockNumber).filter(Boolean))).sort((a, b) => a - b);
      const blockTs = {};
      for (let i = 0; i < uniqBlocks.length; i += 1) {
        const bn = uniqBlocks[i];
        try {
          const b = await prov.getBlock(bn);
          blockTs[bn] = b?.timestamp ?? null;
        } catch {
          blockTs[bn] = null;
        }
        if (i % 25 === 0) setProgress(70 + Math.min(25, Math.round((i / Math.max(1, uniqBlocks.length)) * 25)));
      }

      const withTs = flat.map((e) => ({ ...e, timestamp: blockTs[e.blockNumber] ?? null }));
      withTs.sort((a, b) => (b.blockNumber || 0) - (a.blockNumber || 0));

      setRawEvents(withTs);
      setPage(0);

      setProgress(100);
      addLog(`Done. Synced ${withTs.length} events.`);
      setSnack({ open: true, msg: `Synced ${withTs.length} events`, severity: "success" });
    } catch (e) {
      addLog(`❌ Error: ${e.message}`);
      setSnack({ open: true, msg: `Sync failed: ${e.message}`, severity: "error" });
    } finally {
      setSearching(false);
    }
  }, [
    addLog,
    autoFromBlock,
    computeAutoFromBlock,
    ensureProvider,
    fromBlock,
    maxEvents,
    selectableCatalog,
    selectedEventKeys,
    timeRange,
    toBlock,
  ]);

  const handleNftAudit = useCallback(async () => {
    const body = {
      rpcUrl: (nftAuditForm.rpcUrl || "").trim(),
      txHash: (nftAuditForm.txHash || "").trim(),
      tokenId: (nftAuditForm.tokenId || "").trim(),
      fromBlock: Number(nftAuditForm.fromBlock || 0),
      includeMetadata: true,
    };

    const vaultAddress = (nftAuditForm.vaultAddress || "").trim();
    const nftAddress = (nftAuditForm.nftAddress || "").trim();
    const ethCarbonAddress = (nftAuditForm.ethCarbonAddress || "").trim();
    const sourceRpcUrl = (SRC_RPC || "").trim();
    const sourceEmitterAddress = (BRIDGE_ADDR || "").trim();
    const bridgeReceiverAddress = (BRIDGE_RECEIVER_ADDR || "").trim();

    if (vaultAddress) body.vaultAddress = vaultAddress;
    if (nftAddress) body.nftAddress = nftAddress;
    if (ethCarbonAddress) body.ethCarbonAddress = ethCarbonAddress;
    if (sourceRpcUrl) body.sourceRpcUrl = sourceRpcUrl;
    if (sourceEmitterAddress) body.sourceEmitterAddress = sourceEmitterAddress;
    if (bridgeReceiverAddress) body.bridgeReceiverAddress = bridgeReceiverAddress;

    if (!body.txHash && !body.tokenId) {
      setSnack({ open: true, msg: "Provide txHash or tokenId for NFT audit", severity: "warning" });
      return;
    }

    try {
      setNftAuditLoading(true);
      setNftAuditResult(null);

      const base = String(PROVENANCE_API_BASE || "").replace(/\/+$/, "");
      const response = await fetch(`${base}/api/provenance/audit-nft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || `Audit request failed (${response.status})`);
      }

      setNftAuditResult(data);
      setSnack({ open: true, msg: `NFT audit verdict: ${data.verdict || "UNKNOWN"}`, severity: data.verdict === "FAIL" ? "error" : data.verdict === "WARN" ? "warning" : "success" });
    } catch (e) {
      setSnack({ open: true, msg: `NFT audit failed: ${e.message}`, severity: "error" });
    } finally {
      setNftAuditLoading(false);
    }
  }, [nftAuditForm, SRC_RPC, BRIDGE_ADDR, BRIDGE_RECEIVER_ADDR]);

  const handleNftAuditV2 = useCallback(async () => {
    let txHash = (nftAuditForm.txHash || "").trim();
    const tokenId = (nftAuditForm.tokenId || "").trim();

    if (!txHash && tokenId) {
      try {
        txHash = await resolveMintTxHashByTokenId({
          rpcUrl: (nftAuditForm.rpcUrl || NFT_AUDIT_RPC || "").trim(),
          nftAddress: (nftAuditForm.nftAddress || NFT_AUDIT_NFT || "").trim(),
          tokenId,
        });
      } catch {
        txHash = "";
      }
    }

    if (!txHash) {
      setSnack({ open: true, msg: "Provide txHash for NFT audit V2", severity: "warning" });
      return;
    }

    try {
      setNftAuditV2Loading(true);
      setNftAuditResult(null);

      const bundle = await fetchHepekProvenanceBundle(txHash);
      const normalized = normalizeHepekAuditBundle(bundle);
      setNftAuditResult(normalized);

      const severity = normalized.verdict === "FAIL" ? "error" : normalized.verdict === "WARN" ? "warning" : "success";
      setSnack({ open: true, msg: `NFT audit V2 verdict: ${normalized.verdict}`, severity });
    } catch (e) {
      setSnack({ open: true, msg: `NFT audit V2 failed: ${e.message}`, severity: "error" });
    } finally {
      setNftAuditV2Loading(false);
    }
  }, [NFT_AUDIT_NFT, NFT_AUDIT_RPC, nftAuditForm]);

  // ---------- Role-based event visibility (user-scoped) ----------
  const viewerLower = useMemo(() => (viewerAddress ? viewerAddress.toLowerCase() : ""), [viewerAddress]);

  const viewerDevicesFromEnergy = useMemo(() => {
    const set = new Set();
    if (viewerRole !== "user" || !viewerLower) return set;
    for (const ev of rawEvents) {
      if (ev.source !== "ErgonEnergy") continue;
      if (ev.eventName !== "PowerOutAudit" && ev.eventName !== "PowerInAudit") continue;
      const a = ev.args || {};
      const u = (a.user || a.userID || "").toString().toLowerCase();
      const d = (a.device || "").toString().toLowerCase();
      if (u && u === viewerLower && d) set.add(d);
    }
    return set;
  }, [rawEvents, viewerLower, viewerRole]);

  const viewerMessageIds = useMemo(() => {
    const set = new Set();
    if (viewerRole !== "user" || !viewerLower) return set;

    for (const ev of rawEvents) {
      if (ev.source !== "BridgeEmitter") continue;
      const a = ev.args || {};
      const u = (a.user || "").toString().toLowerCase();
      const mid = (a.messageId || "").toString();
      if (mid && u && u === viewerLower) set.add(mid);
    }

    for (const ev of rawEvents) {
      if (ev.source !== "BridgeEmitter") continue;
      if (ev.eventName !== "BridgeInitiatedV3") continue;
      const a = ev.args || {};
      const mid = (a.messageId || "").toString();
      if (mid && set.has(mid)) set.add(mid);
    }
    return set;
  }, [rawEvents, viewerLower, viewerRole]);

  const eventsByRole = useMemo(() => {
    if (viewerRole !== "user") return rawEvents;
    if (!viewerLower) return [];

    return rawEvents.filter((ev) => {
      const meta = eventMeta?.[ev.source]?.[ev.eventName];
      if (!meta) return false;
      if (!roleAtLeast(viewerRole, meta.minRole)) return false;
      if (meta.userSelectable !== true) return false;

      const a = ev.args || {};

      if (ev.source === "ErgonEnergy") {
        if (ev.eventName === "PowerOutAudit" || ev.eventName === "PowerInAudit" || ev.eventName === "RedeemCarbon") {
          const u = (a.user || a.userID || "").toString().toLowerCase();
          const d = (a.device || a.hepekID || "").toString().toLowerCase();
          return (u && u === viewerLower) || (d && d === viewerLower);
        }
        if (ev.eventName === "DemandExportBreached") {
          const d = (a.hepekID || a.device || "").toString().toLowerCase();
          if (d && d === viewerLower) return true;
          return d && viewerDevicesFromEnergy.has(d);
        }
        return false;
      }

      if (ev.source === "Carbon") {
        const holder = (a.holder || "").toString().toLowerCase();
        const beneficiary = (a.beneficiaryUser || a.user || "").toString().toLowerCase();
        const from = (a.from || "").toString().toLowerCase();
        const to = (a.to || "").toString().toLowerCase();

        if (ev.eventName === "CarbonMintLotDelta") return holder === viewerLower || beneficiary === viewerLower;
        if (ev.eventName === "PeriodSecondsUpdated") return false;
        if (ev.eventName === "CarbonLotTransfer") return from === viewerLower || to === viewerLower;
        if (ev.eventName === "CarbonLotBurn") return from === viewerLower || (a.operator || "").toString().toLowerCase() === viewerLower;
        if (ev.eventName === "CarbonLotFrozen" || ev.eventName === "CarbonLotUnfrozen") return holder === viewerLower;
        if (ev.eventName === "CarbonMint" || ev.eventName === "CarbonBurn") {
          const u = (a.user || "").toString().toLowerCase();
          const d = (a.device || "").toString().toLowerCase();
          return u === viewerLower || d === viewerLower;
        }
        return false;
      }

      if (ev.source === "BridgeEmitter") {
        const mid = (a.messageId || "").toString();
        if (a.user) {
          const u = (a.user || "").toString().toLowerCase();
          return u === viewerLower;
        }
        return mid && viewerMessageIds.has(mid);
      }

      return false;
    });
  }, [eventMeta, rawEvents, viewerDevicesFromEnergy, viewerLower, viewerMessageIds, viewerRole]);

  // Sync events (role-filtered) to global window for cross-component access (AuditChainChase)
  useEffect(() => {
    window.__provenanceEvents = Array.isArray(eventsByRole) ? eventsByRole : [];
  }, [eventsByRole]);

  // Persistent devices object (across searches and reloads)
  const [devices, setDevices] = useState(() => {
    try {
      const saved = localStorage.getItem("provenanceDevices");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const devicesRef = useRef(devices);

  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  // Update devices only when UserAdded events are present
  useEffect(() => {
    const newDevices = { ...devices };
    let updated = false;
    eventsByRole.forEach((ev) => {
      if (ev.eventName === "UserAdded" /*|| ev.eventName === "UserGeoUpdated"*/) {
        const lat = ev.latitude || (ev.args && (ev.args.latitude || ev.args.lat));
        const lon = ev.longitude || (ev.args && (ev.args.longitude || ev.args.lon));
        const deviceId = ev.args[6] || (ev.args[6] && ev.args.hepekID) || ev.id;
        const hepekId = ev.args[0];
        if (lat && lon && deviceId) {
          newDevices[deviceId] = {
            deviceId,
            hepekId,
            latitude: Number(lat) / 1e6 - 90,
            longitude: Number(lon) / 1e6,
            user: ev.user || (ev.args && ev.args[2]),
          };
          updated = true;
        }
      }
    });
    if (updated) {
      setDevices(newDevices);
      try {
        localStorage.setItem("provenanceDevices", JSON.stringify(newDevices));
      } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsByRole.filter(ev => ev.eventName === "UserAdded").length]);


  useEffect(() => {
    if (!ENERGY_ADDR || !provider || !energyAbi?.abi) return;

    let cancelled = false;
    const contract = new ethers.Contract(ENERGY_ADDR, energyAbi.abi, provider);

    async function pollErrors() {
      const current = devicesRef.current || {};
      const entries = Object.entries(current);
      if (!entries.length) return;

      const next = { ...current };
      let changed = false;

      for (const [deviceId, dev] of entries) {
        const hepekId = dev.hepekId || dev.deviceId;
        if (!hepekId) continue;

        let errorFlag = true;
        try {
          const user = await contract.userlist(hepekId);
          const lastError =
            user?.lastError?.toNumber ? user.lastError.toNumber() : Number(user?.lastError || 0);
          errorFlag = lastError !== 0;
        } catch {
          errorFlag = true;
        }

        const prevFlag = Boolean(current?.[deviceId]?.error);
        if (prevFlag !== Boolean(errorFlag)) {
          next[deviceId] = { ...dev, error: Boolean(errorFlag) };
          changed = true;
        }
      }

      if (!cancelled && changed) {
        setDevices(next);
      }
    }

    pollErrors(); // run once
    const interval = setInterval(pollErrors, 60_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [ENERGY_ADDR, provider, energyAbi]);


  // Device locations as array for map, etc.
  const deviceLocations = useMemo(() => Object.values(devices), [devices]);

  // ---------- Search filter / sort / pagination uses role-filtered events ----------
  const filteredEvents = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    let filtered = eventsByRole;
    if (selectedDevice) {
      filtered = filtered.filter(ev => {
        const args = ev.args || {};
        // Match deviceId or hepekId
        return args.device === selectedDevice || args.hepekID === selectedDevice || args.id === selectedDevice;
      });
    }
    if (!s) return filtered;
    return filtered.filter((ev) => {
      if ((ev.transactionHash || "").toLowerCase().includes(s)) return true;
      if ((ev.eventName || "").toLowerCase().includes(s)) return true;
      if ((ev.source || "").toLowerCase().includes(s)) return true;
      const args = ev.args || {};
      return Object.values(args).some((v) => (v != null ? v.toString().toLowerCase().includes(s) : false));
    });
  }, [eventsByRole, search, selectedDevice]);

  const sortedEvents = useMemo(() => {
    const arr = filteredEvents.slice();
    const dir = order === "asc" ? 1 : -1;

    arr.sort((a, b) => {
      if (orderBy === "timestamp") return ((a.timestamp || 0) - (b.timestamp || 0)) * dir;
      if (orderBy === "eventName") return String(a.eventName).localeCompare(String(b.eventName)) * dir;
      if (orderBy === "source") return String(a.source).localeCompare(String(b.source)) * dir;
      return ((a.blockNumber || 0) - (b.blockNumber || 0)) * dir;
    });

    return arr;
  }, [filteredEvents, order, orderBy]);

  const pagedEvents = useMemo(() => {
    return sortedEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, sortedEvents]);

  // ---------- Stats ----------
  const stats = useMemo(() => {
    const total = filteredEvents.length;
    const lastBlock = total ? Math.max(...filteredEvents.map((e) => e.blockNumber || 0)) : 0;
    const lastTs = total ? Math.max(...filteredEvents.map((e) => e.timestamp || 0)) : 0;

    const uniqDevices = new Set();
    const uniqUsers = new Set();
    filteredEvents.forEach((e) => {
      const a = e.args || {};
      if (a.device) uniqDevices.add(String(a.device));
      if (a.hepekID) uniqDevices.add(String(a.hepekID));
      if (a.holder) uniqDevices.add(String(a.holder));
      if (a.from) uniqDevices.add(String(a.from));

      if (a.user) uniqUsers.add(String(a.user));
      if (a.userID) uniqUsers.add(String(a.userID));
      if (a.to) uniqUsers.add(String(a.to));
      if (a.operator) uniqUsers.add(String(a.operator));
    });

    return { total, lastBlock, lastTs, uniqDevices: uniqDevices.size, uniqUsers: uniqUsers.size };
  }, [filteredEvents]);

  // ---------- Audit dashboard (use role-filtered but NOT search-filtered for integrity) ----------
  const auditBaseEvents = useMemo(() => eventsByRole, [eventsByRole]);

  const auditMetrics = useMemo(() => {
    const total = auditBaseEvents.length;

    const ts = auditBaseEvents.map((e) => e.timestamp).filter(Boolean);
    const minTs = ts.length ? Math.min(...ts) : 0;
    const maxTs = ts.length ? Math.max(...ts) : 0;

    const readingHashes = [];
    const provenanceHashes = [];
    let missingTs = 0;

    let demandBreaches = 0;

    let oracleUpdates = 0;
    let lastErgonPrice = null;
    let lastCarbonPrice = null;

    const bridgeState = new Map(); // messageId -> state
    let bridgeSuccess = 0;
    let bridgeFail = 0;
    let bridgeRefund = 0;

    let periodSecondsUpdated = 0;
    let lotMintDeltas = 0;
    let lotBurns = 0;
    let lotTransfers = 0;
    let lotFrozen = 0;
    let lotUnfrozen = 0;
    let periodClosed = 0;
    let deviceCommits = 0;
    let globalCommits = 0;

    for (const ev of auditBaseEvents) {
      if (!ev.timestamp) missingTs += 1;

      const a = ev.args || {};

      if (ev.source === "ErgonEnergy") {
        if (ev.eventName === "PowerOutAudit" || ev.eventName === "PowerInAudit") {
          if (a.readingHash) readingHashes.push(String(a.readingHash));
          if (a.provenanceHash) provenanceHashes.push(String(a.provenanceHash));
        }
        if (ev.eventName === "DemandExportBreached") demandBreaches += 1;
        if (ev.eventName === "OracleUpdated") {
          oracleUpdates += 1;
          lastErgonPrice = a.ergonPrice != null ? safeToNumberUnits(a.ergonPrice, 18) : lastErgonPrice;
          lastCarbonPrice = a.carbonPrice != null ? safeToNumberUnits(a.carbonPrice, 18) : lastCarbonPrice;
        }
        if (ev.eventName === "PowerOutAudit") {
          if (a.ergonPrice != null) lastErgonPrice = safeToNumberUnits(a.ergonPrice, 18);
          if (a.carbonPrice != null) lastCarbonPrice = safeToNumberUnits(a.carbonPrice, 18);
        }
      }

      if (ev.source === "Carbon") {
        if (ev.eventName === "CarbonMintLotDelta") lotMintDeltas += 1;
        if (ev.eventName === "PeriodSecondsUpdated") periodSecondsUpdated += 1;
        if (ev.eventName === "CarbonLotBurn") lotBurns += 1;
        if (ev.eventName === "CarbonLotTransfer") lotTransfers += 1;
        if (ev.eventName === "CarbonLotFrozen") lotFrozen += 1;
        if (ev.eventName === "CarbonLotUnfrozen") lotUnfrozen += 1;
        if (ev.eventName === "PeriodClosed") periodClosed += 1;
        if (ev.eventName === "DevicePeriodCommitted") deviceCommits += 1;
        if (ev.eventName === "PeriodGlobalCommitted") globalCommits += 1;
      }

      if (ev.source === "BridgeEmitter") {
        const mid = a.messageId ? String(a.messageId) : "";
        if (mid) {
          if (!bridgeState.has(mid)) bridgeState.set(mid, { locked: false, finalized: false, success: null, unlocked: false, burned: false, refund: false });
          const st = bridgeState.get(mid);

          if (ev.eventName === "TokensLocked") st.locked = true;
          if (ev.eventName === "BridgeFinalized") {
            st.finalized = true;
            st.success = Boolean(a.success);
            if (st.success) bridgeSuccess += 1;
            else bridgeFail += 1;
          }
          if (ev.eventName === "TokensUnlocked") st.unlocked = true;
          if (ev.eventName === "TokensBurned") st.burned = true;
          if (ev.eventName === "AutoRefundProcessed") {
            st.refund = true;
            bridgeRefund += 1;
          }
        }
      }
    }

    const uniqReading = new Set(readingHashes);
    const dupReading = readingHashes.length - uniqReading.size;

    const uniqProv = new Set(provenanceHashes);
    const dupProv = provenanceHashes.length - uniqProv.size;

    let inFlight = 0;
    let finalized = 0;
    let burned = 0;
    let unlocked = 0;
    let refunds = 0;
    for (const st of bridgeState.values()) {
      if (st.locked && !st.finalized) inFlight += 1;
      if (st.finalized) finalized += 1;
      if (st.burned) burned += 1;
      if (st.unlocked) unlocked += 1;
      if (st.refund) refunds += 1;
    }

    return {
      total,
      minTs,
      maxTs,
      missingTs,
      readingTotal: readingHashes.length,
      readingDup: dupReading,
      provTotal: provenanceHashes.length,
      provDup: dupProv,
      demandBreaches,
      oracleUpdates,
      lastErgonPrice,
      lastCarbonPrice,
      bridge: { inFlight, finalized, burned, unlocked, refunds, success: bridgeSuccess, fail: bridgeFail },
      lots: { lotMintDeltas, lotBurns, lotTransfers, lotFrozen, lotUnfrozen, periodClosed, deviceCommits, globalCommits },
    };
  }, [auditBaseEvents]);

  const dailyBuckets = useMemo(() => bucketByDay(auditBaseEvents), [auditBaseEvents]);
  const bridgeDaily = useMemo(() => bucketBridgeFinalizedByDay(auditBaseEvents), [auditBaseEvents]);

  // ---------- Analytics dropdown sync ----------
  const selectedEventNames = useMemo(() => {
    const names = selectedEventKeys.map((k) => k.split(":")[1]).filter(Boolean);
    return Array.from(new Set(names));
  }, [selectedEventKeys]);

  const existingEventNames = useMemo(() => {
    const names = filteredEvents.map((e) => e.eventName).filter(Boolean);
    return Array.from(new Set(names)).sort();
  }, [filteredEvents]);

  const analyticsModeOptions = useMemo(() => {
    const setExisting = new Set(existingEventNames);
    const intersection = selectedEventNames.filter((n) => setExisting.has(n));
    if (intersection.length) return intersection.sort();
    if (selectedEventNames.length) return selectedEventNames.sort();
    return existingEventNames;
  }, [existingEventNames, selectedEventNames]);

  useEffect(() => {
    if (!analyticsModeOptions.length) {
      setAnalyticsMode("");
      return;
    }
    setAnalyticsMode((prev) => (prev && analyticsModeOptions.includes(prev) ? prev : analyticsModeOptions[0]));
  }, [analyticsModeOptions]);

  const analyticsList = useMemo(() => {
    if (!analyticsMode) return [];
    return filteredEvents
      .filter((e) => e.eventName === analyticsMode)
      .slice()
      .sort((a, b) => (a.blockNumber || 0) - (b.blockNumber || 0));
  }, [filteredEvents, analyticsMode]);

  const labels = useMemo(() => makeSparseLabels(analyticsList, 10), [analyticsList]);

  const chartOptions = useMemo(() => {
    const grid = theme.palette.divider;
    const text = theme.palette.text.secondary;
    const tooltipBg = theme.palette.background.paper;
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: theme.palette.text.primary, usePointStyle: true } },
        tooltip: {
          backgroundColor: tooltipBg,
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.primary,
          borderColor: grid,
          borderWidth: 1,
        },
      },
      scales: {
        x: { ticks: { color: text }, grid: { color: grid } },
        y: { ticks: { color: text }, grid: { color: grid } },
      },
    };
  }, [theme]);

  const powerOutSeries = useMemo(() => {
    if (analyticsMode !== "PowerOutAudit") return null;
    const ex = analyticsList.map((e) => scale(e.args?._export, SCALE.meter));
    const im = analyticsList.map((e) => scale(e.args?._import, SCALE.meter));
    const produced = ex.map((v, i) => (v || 0) - (im[i] || 0));
    const ergonMint = analyticsList.map((e) => scale(e.args?.ergonMint, SCALE.mint));
    const carbonMint = analyticsList.map((e) => scale(e.args?.carbonMint, SCALE.mint));
    const deltaKw = analyticsList.map((e) => scale(e.args?.delta, SCALE.delta));
    const ergonPrice = analyticsList.map((e) => safeToNumberUnits(e.args?.ergonPrice, 18) || 0);
    const carbonPrice = analyticsList.map((e) => safeToNumberUnits(e.args?.carbonPrice, 18) || 0);
    const threshold = analyticsList.map((e) => safeToNumberUnits(e.args?.threshold, 18) || 0);
    return { ex, im, produced, ergonMint, carbonMint, deltaKw, ergonPrice, carbonPrice, threshold };
  }, [analyticsList, analyticsMode, scale]);

  // ---------- UI actions ----------
  const handleSort = useCallback(
    (field) => {
      const isAsc = orderBy === field && order === "asc";
      setOrderBy(field);
      setOrder(isAsc ? "desc" : "asc");
    },
    [order, orderBy]
  );

  const openDetails = useCallback((ev) => {
    setSelectedEvent(ev);
    setDetailOpen(true);
  }, []);

  // ---------- Export ----------
  const exportCSV = useCallback(() => {
    const header = ["time", "block", "source", "event", "txHash", "primaryId", "device", "user", "amount", "summary"];
    const lines = [header.join(",")];

    sortedEvents.forEach((ev) => {
      const p = primaryId(ev);
      const { device, user, amount, summary } = eventSummary(ev, { scale, safeFmtUnits });
      lines.push(
        [
          csvEscape(ev.timestamp ? fmtTs(ev.timestamp) : ""),
          ev.blockNumber ?? "",
          csvEscape(ev.source),
          csvEscape(ev.eventName),
          csvEscape(ev.transactionHash || ""),
          csvEscape(p || ""),
          csvEscape(device || ""),
          csvEscape(user || ""),
          csvEscape(amount || ""),
          csvEscape(summary || ""),
        ].join(",")
      );
    });

    downloadBlob(`provenance-export-${new Date().toISOString().slice(0, 10)}.csv`, lines.join("\n"), "text/csv;charset=utf-8");
    setSnack({ open: true, msg: "CSV exported", severity: "success" });
  }, [safeFmtUnits, scale, sortedEvents]);

  const exportPDF = useCallback(async () => {
    try {
      const jsPDF = (await import("jspdf")).default;
      const doc = new jsPDF();

      const pageH = doc.internal.pageSize.height;
      const pageW = doc.internal.pageSize.width;
      const left = 14;
      let y = 18;

      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.text("Provenance Report", left, y);
      y += 8;

      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(`Generated: ${new Date().toLocaleString()}`, left, y);
      y += 6;

      doc.text(`Viewer: ${viewerRole}${viewerAddress ? ` (${viewerAddress})` : ""}`, left, y);
      y += 6;

      const selectionLabel = selectedEventKeys.length <= 3 ? selectedEventKeys.join(", ") : `${selectedEventKeys.length} event types`;
      doc.text(`Selection: ${selectionLabel}`, left, y);
      y += 6;

      doc.text(`Filters: range=${timeRange}, autoFromBlock=${autoFromBlock ? "yes" : "no"}, fromBlock=${fromBlock || 0}, toBlock=${toBlock}`, left, y);
      y += 6;

      doc.text(`Search="${search || ""}" | Rows=${sortedEvents.length}`, left, y);
      y += 8;

      doc.setFontSize(8);
      doc.setTextColor(255);
      doc.setFillColor(76, 175, 80);
      doc.rect(left, y, pageW - left * 2, 7, "F");
      doc.text("Time", left + 2, y + 5);
      doc.text("Blk", left + 28, y + 5);
      doc.text("Src", left + 40, y + 5);
      doc.text("Event", left + 56, y + 5);
      doc.text("Tx", left + 96, y + 5);
      doc.text("Primary", left + 124, y + 5);
      y += 9;

      doc.setTextColor(0);

      const maxRows = 600;
      const rows = sortedEvents.slice(0, maxRows);

      for (let i = 0; i < rows.length; i += 1) {
        if (y > pageH - 12) {
          doc.addPage();
          y = 16;
        }

        const ev = rows[i];
        const time = ev.timestamp ? new Date(ev.timestamp * 1000).toLocaleDateString() : "";
        const blk = String(ev.blockNumber ?? "");
        const src = (ev.source || "").slice(0, 10);
        const name = (ev.eventName || "").slice(0, 20);
        const tx = short(ev.transactionHash || "");
        const pid = short(primaryId(ev) || "");

        if (i % 2 === 0) {
          doc.setFillColor(245);
          doc.rect(left, y - 5, pageW - left * 2, 6, "F");
        }

        doc.text(time, left + 2, y);
        doc.text(blk, left + 28, y);
        doc.text(src, left + 40, y);
        doc.text(name, left + 56, y);
        doc.text(tx, left + 96, y);
        doc.text(pid, left + 124, y);
        y += 6;
      }

      if (sortedEvents.length > maxRows) {
        if (y > pageH - 12) {
          doc.addPage();
          y = 16;
        }
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(`Note: truncated to first ${maxRows} rows for readability.`, left, y);
      }

      doc.save(`provenance-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      setSnack({ open: true, msg: "PDF exported", severity: "success" });
    } catch (e) {
      setSnack({ open: true, msg: `PDF export failed: ${e.message}`, severity: "error" });
    }
  }, [autoFromBlock, fromBlock, search, selectedEventKeys, sortedEvents, timeRange, toBlock, viewerAddress, viewerRole]);

  // ---------- UI components ----------
  const ProviderChip = useCallback(() => {
    const ok = Boolean(provider);
    return (
      <Chip
        icon={ok ? <Wifi /> : <WifiOff />}
        label={ok ? (providerKind === "ws" ? "Live (WS)" : "Connected (RPC)") : "Offline"}
        color={ok ? "success" : "default"}
        variant="outlined"
        size="small"
      />
    );
  }, [provider, providerKind]);

  const RoleChip = useCallback(() => {
    const icon =
      viewerRole === "admin" ? <AdminPanelSettings /> : viewerRole === "auditor" || viewerRole === "public_auditor" ? <Gavel /> : <Person />;
    const label = viewerRole === "public_auditor" ? "auditor (public)" : viewerRole;
    return (
      <Chip
        icon={icon}
        label={label}
        color={viewerRole === "admin" ? "warning" : viewerRole === "auditor" || viewerRole === "public_auditor" ? "info" : "default"}
        variant="outlined"
        size="small"
      />
    );
  }, [viewerRole]);

  const QuickRangeChips = useCallback(
    () => (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Chip label="Last 1h" size="small" onClick={() => { setTimeRange("hour"); setAutoFromBlock(true); }} color={timeRange === "hour" ? "primary" : "default"} variant={timeRange === "hour" ? "filled" : "outlined"} />
        <Chip label="Last 24h" size="small" onClick={() => { setTimeRange("day"); setAutoFromBlock(true); }} color={timeRange === "day" ? "primary" : "default"} variant={timeRange === "day" ? "filled" : "outlined"} />
        <Chip label="Last 7d" size="small" onClick={() => { setTimeRange("week"); setAutoFromBlock(true); }} color={timeRange === "week" ? "primary" : "default"} variant={timeRange === "week" ? "filled" : "outlined"} />
        <Chip label="All" size="small" onClick={() => { setTimeRange("all"); setAutoFromBlock(true); }} color={timeRange === "all" ? "primary" : "default"} variant={timeRange === "all" ? "filled" : "outlined"} />
        <Chip label="Sync now" size="small" icon={<Refresh />} onClick={handleSync} color="success" variant="outlined" disabled={searching || !hasProviderConfig} />
      </Box>
    ),
    [handleSync, hasProviderConfig, searching, timeRange]
  );

  const PresetChips = useCallback(
    () => (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Chip label="Core" size="small" variant="outlined" onClick={() => applyPreset("core")} />
        <Chip label="Lots" size="small" variant="outlined" onClick={() => applyPreset("lots")} />
        <Chip label="Bridge" size="small" variant="outlined" onClick={() => applyPreset("bridge")} />
        {viewerRole !== "user" && <Chip label="Policy/Gov" size="small" variant="outlined" onClick={() => applyPreset("policy")} />}
        <Divider orientation="vertical" flexItem />
        <Chip label="All" size="small" color="primary" variant="outlined" onClick={() => applyPreset("all")} />
        <Chip label="Clear" size="small" color="error" variant="outlined" onClick={() => applyPreset("clear")} />
      </Box>
    ),
    [applyPreset, viewerRole]
  );

  // ---------- reading hash verifier ----------
  const calcReadingHash = useCallback((payload) => {
    if (!payload) return null;
    try {
      // Backend sometimes returns wrappers like { payload: { ... } }
      let p = payload;
      if (p && typeof p === "object" && p.payload && typeof p.payload === "object") {
        // Heuristic: unwrap if nested payload contains the meter_reading object
        const inner = p.payload;
        if (inner.meter_reading || inner.meterReading || inner.meter || inner.payload) p = inner;
      }

      // Support multiple shapes: {meter_reading:{...}} or {payload:{meter_reading:{...}}}
      const m =
        p.meter_reading ||
        p.meterReading ||
        p.meter ||
        (p.payload && (p.payload.meter_reading || p.payload.meterReading || p.payload.meter)) ||
        {};

      const tsRaw = m.timestamp ?? p.timestamp;
      const epoch = parseAnyTimestampToEpochSeconds(tsRaw);
      const timestamp = Math.floor(epoch / 60) * 60; // minute bucket (matches device)

      const dir = String(m.direction ?? p.direction ?? "").toUpperCase() || "OUT";
      const ZERO_HASH = "0x" + "0".repeat(64);

      let prev_hash =
        m.previous_reading_hash ??
        m.previousReadingHash ??
        m.prev_hash ??
        m.prevHash ??
        m.previous_record_hash ??
        m.previousRecordHash ??
        p.previous_reading_hash ??
        p.prev_hash ??
        ZERO_HASH;

      prev_hash = typeof prev_hash === "string" && prev_hash.trim() ? prev_hash.trim() : ZERO_HASH;
      if (dir === "GENESIS") prev_hash = ZERO_HASH;

      const factor = Number(m.factor ?? m.FACTOR ?? p.factor ?? p.FACTOR ?? 0);

      const produced_power = Number(
        m.produced_power ?? m.produced ?? m.producedPower ?? p.produced_power ?? p.produced ?? p.producedPower ?? 0
      );
      const consumed_power = Number(
        m.consumed_power ?? m.consumed ?? m.consumedPower ?? p.consumed_power ?? p.consumed ?? p.consumedPower ?? 0
      );

      const produced = dir === "IN" ? 0 : Math.trunc(produced_power);
      const consumed = dir === "IN" ? Math.trunc(consumed_power) : 0;

      const device_id = String(p.device_id ?? p.deviceId ?? p.device ?? "").trim();

      // Must match device canonical JSON ordering (sorted keys, no extra spaces)
      const obj = {
        consumed,
        device_id,
        direction: dir,
        factor: Math.trunc(factor),
        prev_hash,
        produced,
        schema: "READING_V1",
        timestamp,
      };

      return keccak256(toUtf8Bytes(JSON.stringify(obj)));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Error calculating reading hash:", e);
      return null;
    }
  }, []);



  const verifyTxSender = useCallback(
    async (txHash, expectedFrom) => {
      setTxSenderLoading(true);
      setTxSenderError(null);
      setTxSenderStatus(null);

      try {
        if (!txHash) throw new Error("tx hash missing");
        const expected = String(expectedFrom || "").toLowerCase();
        if (!expected) throw new Error("expected sender missing");

        const prov = await ensureProvider();
        if (!prov) throw new Error("provider not available");

        const tx = await prov.getTransaction(txHash);
        const from = String(tx?.from || "").toLowerCase();

        setTxSenderInfo({ from, expected });

        if (from && expected && from === expected) setTxSenderStatus("ok");
        else setTxSenderStatus("fail");
      } catch (e) {
        setTxSenderError(e.message || String(e));
        setTxSenderStatus("error");
      } finally {
        setTxSenderLoading(false);
      }
    },
    [ensureProvider]
  );

  // ---------- Render ----------
  return (
    <ProviderContext.Provider value={provider}>
      <LogContext.Provider value={{ log, addLog }}>
        <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  🔎 Blockchain Provenance Explorer
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {viewerRole === "user"
                    ? "User view: only your events are visible & searchable."
                    : "Auditor/Admin view: full provenance and governance surface."}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <RoleChip />
                {viewerAddress ? (
                  <Chip label={short(viewerAddress)} size="small" variant="outlined" />
                ) : (
                  <Button variant="outlined" size="small" onClick={handleConnectWallet}>
                    Connect wallet
                  </Button>
                )}

                {canSwitchRole && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Role</InputLabel>
                    <Select label="Role" value={viewerRole} onChange={(e) => setViewerRole(e.target.value)}>
                      <MenuItem value="user">user</MenuItem>
                      <MenuItem value="auditor">auditor</MenuItem>
                      {resolveRoleFromWallet(viewerAddress) === "admin" && <MenuItem value="admin">admin</MenuItem>}
                      {ALLOW_PUBLIC_AUDITOR && <MenuItem value="public_auditor">auditor (public)</MenuItem>}
                    </Select>
                  </FormControl>
                )}

                <ProviderChip />
                <Button variant="contained" startIcon={<Refresh />} onClick={handleSync} disabled={searching || !hasProviderConfig}>
                  Sync
                </Button>
                <Button variant="outlined" startIcon={<GetApp />} onClick={exportCSV} disabled={!sortedEvents.length}>
                  Export CSV
                </Button>
                <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={exportPDF} disabled={!sortedEvents.length}>
                  Export PDF
                </Button>
              </Box>
            </Box>

            {/* Device filter dropdown 
            <FormControl sx={{ minWidth: 220, mb: 2 }} size="small">
              <InputLabel id="device-select-label">Device</InputLabel>
              <Select
                labelId="device-select-label"
                value={selectedDevice}
                label="Device"
                onChange={e => setSelectedDevice(e.target.value)}
              >
                <MenuItem value=""><em>All Devices</em></MenuItem>
                {Object.values(devices).map(dev => (
                  <MenuItem key={dev.deviceId || dev.hepekId || dev.id} value={dev.deviceId || dev.hepekId || dev.id}>
                    {dev.deviceId || dev.hepekId || dev.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            */}
            {!hasProviderConfig && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Missing config. Set <code>REACT_APP_SOURCE_RPC_URL</code> or <code>REACT_APP_SOURCE_WS_URL</code> and <code>REACT_APP_SOURCE_ERGONENERGY_CONTRACT_ADDRESS</code>.
              </Alert>
            )}

            {viewerRole === "user" && !viewerAddress && !ALLOW_PUBLIC_AUDITOR && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Connect wallet to view your scoped events.
              </Alert>
            )}
          </Box>

          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Events (filtered)
                  </Typography>
                  <Typography variant="h6">{sortedEvents.length.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Latest block
                  </Typography>
                  <Typography variant="h6">{stats.lastBlock ? stats.lastBlock.toLocaleString() : "—"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Last timestamp
                  </Typography>
                  <Typography variant="h6">{stats.lastTs ? fmtTs(stats.lastTs) : "—"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    Unique devices / users
                  </Typography>
                  <Typography variant="h6">
                    {stats.uniqDevices} / {stats.uniqUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Main tabs */}
          <Paper sx={{ mb: 2 }}>
            <Box sx={{ px: 2, pt: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="Provenance tabs">
                <Tab icon={<TableChart />} iconPosition="start" label="Events" sx={{ textTransform: "none" }} />
                <Tab icon={<Analytics />} iconPosition="start" label="Audit & Analytics" sx={{ textTransform: "none" }} />
                <Tab icon={<BugReport />} iconPosition="start" label="Diagnostics" sx={{ textTransform: "none" }} />
                <Tab icon={<Gavel />} iconPosition="start" label="NFT Audit" sx={{ textTransform: "none" }} />
                <Tab icon={<Public />} iconPosition="start" label="Geolocations" sx={{ textTransform: "none" }} />
              </Tabs>
              <Chip icon={provider ? <Wifi /> : <WifiOff />} label={provider ? "Connected" : "Offline"} color={provider ? "success" : "default"} size="small" variant="outlined" />
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }} />

            <Box sx={{ p: 2 }}>
              {/* Events tab */}
              {tab === 0 && (
                <>
                  {searching && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: "right" }}>
                          {progress}%
                        </Typography>
                      </Box>
                    </Paper>
                  )}

                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">

                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Search events"
                          placeholder="tx hash, readingHash, provenanceHash, messageId, lotId, device, user…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          InputProps={{
                            startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                            endAdornment: search ? (
                              <Tooltip title="Clear">
                                <IconButton size="small" onClick={() => setSearch("")}>
                                  <Clear />
                                </IconButton>
                              </Tooltip>
                            ) : null,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="device-select-label">Device</InputLabel>
                          <Select
                            labelId="device-select-label"
                            value={selectedDevice}
                            label="Device"
                            onChange={e => setSelectedDevice(e.target.value)}

                          >
                            <MenuItem value=""><em>All Devices</em></MenuItem>
                            {Object.values(devices)
                              .filter(dev => viewerRole !== "user" || (viewerAddress && dev.user && dev.user.toLowerCase() === viewerAddress.toLowerCase()))
                              .map(dev => (
                                <MenuItem key={dev.deviceId || dev.hepekId || dev.id} value={dev.hepekId}>
                                  {devices[dev.deviceId]?.deviceId || devices[dev.deviceId]?.hepekId || dev.deviceId || dev.hepekId || dev.id}
                                </MenuItem>
                              ))}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Device-filtered: {viewerRole === "user" ? "only your relevant device" : "full audit surface"}.
                          </Typography>

                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Event Types</InputLabel>
                          <Select
                            multiple
                            label="Event Types"
                            value={selectedEventKeys}
                            onChange={(e) => {
                              const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                              setSelectedEventKeys(value.filter((k) => validSelectableKeys.has(k)));
                            }}
                            renderValue={(selected) => {
                              if (!selected.length) return "None";
                              if (selected.length <= 3) return selected.join(", ");
                              return `${selected.length} selected`;
                            }}
                            MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
                          >
                            {selectableCatalog.flatMap((c) => [
                              <ListSubheader key={`h-${c.source}`}>{c.source}</ListSubheader>,
                              ...c.events.map((ev) => {
                                const key = `${c.source}:${ev}`;
                                return (
                                  <MenuItem key={key} value={key}>
                                    <Checkbox checked={selectedEventKeys.indexOf(key) > -1} />
                                    <ListItemText primary={ev} secondary={c.source} />
                                  </MenuItem>
                                );
                              }),
                            ])}
                          </Select>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Role-filtered: {viewerRole === "user" ? "only your relevant event types" : "full audit surface"}.
                          </Typography>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button variant="outlined" startIcon={showFilters ? <FilterAltOff /> : <FilterAlt />} onClick={() => setShowFilters((v) => !v)} fullWidth>
                          Filters
                          {(autoFromBlock === false || maxEvents !== 2500 || (toBlock && toBlock !== "latest")) && <Badge color="primary" variant="dot" sx={{ ml: 1 }} />}
                        </Button>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                      <Insights fontSize="small" /> Presets
                    </Typography>
                    <PresetChips />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                      <Insights fontSize="small" /> Quick Range
                    </Typography>
                    <QuickRangeChips />

                    <Collapse in={showFilters}>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Time Range</InputLabel>
                            <Select label="Time Range" value={timeRange} onChange={(e) => { setTimeRange(e.target.value); setAutoFromBlock(true); }}>
                              <MenuItem value="hour">Last 1 hour</MenuItem>
                              <MenuItem value="day">Last 1 day</MenuItem>
                              <MenuItem value="week">Last 1 week</MenuItem>
                              <MenuItem value="month">Last 1 month</MenuItem>
                              <MenuItem value="3months">Last 3 months</MenuItem>
                              <MenuItem value="year">Last 1 year</MenuItem>
                              <MenuItem value="all">All</MenuItem>
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary">
                            Auto fromBlock uses avg block-time sample.
                          </Typography>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="From block"
                            value={fromBlock}
                            onChange={(e) => { setFromBlock(e.target.value.replace(/[^\d]/g, "")); setAutoFromBlock(false); }}
                            helperText={autoFromBlock ? "auto (computed)" : "manual"}
                          />
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Button size="small" variant={autoFromBlock ? "contained" : "outlined"} onClick={() => setAutoFromBlock(true)} fullWidth>
                              Auto
                            </Button>
                            <Button size="small" variant={!autoFromBlock ? "contained" : "outlined"} onClick={() => setAutoFromBlock(false)} fullWidth>
                              Manual
                            </Button>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField fullWidth size="small" label="To block" value={toBlock} onChange={(e) => setToBlock(e.target.value)} helperText='Use "latest" or a number' />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField fullWidth size="small" label="Max events" type="number" value={maxEvents} onChange={(e) => setMaxEvents(Math.max(100, Number(e.target.value || 0)))} helperText="UI safety cap" />
                        </Grid>
                      </Grid>
                    </Collapse>
                  </Paper>

                  {/* Table */}
                  <Paper>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TableChart /> Events
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sortedEvents.length.toLocaleString()} rows
                      </Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sortDirection={orderBy === "blockNumber" ? order : false}>
                              <TableSortLabel active={orderBy === "blockNumber"} direction={orderBy === "blockNumber" ? order : "asc"} onClick={() => handleSort("blockNumber")}>
                                Block
                              </TableSortLabel>
                            </TableCell>

                            <TableCell sortDirection={orderBy === "timestamp" ? order : false}>
                              <TableSortLabel active={orderBy === "timestamp"} direction={orderBy === "timestamp" ? order : "asc"} onClick={() => handleSort("timestamp")}>
                                Timestamp
                              </TableSortLabel>
                            </TableCell>

                            <TableCell sortDirection={orderBy === "source" ? order : false}>
                              <TableSortLabel active={orderBy === "source"} direction={orderBy === "source" ? order : "asc"} onClick={() => handleSort("source")}>
                                Source
                              </TableSortLabel>
                            </TableCell>

                            <TableCell sortDirection={orderBy === "eventName" ? order : false}>
                              <TableSortLabel active={orderBy === "eventName"} direction={orderBy === "eventName" ? order : "asc"} onClick={() => handleSort("eventName")}>
                                Event
                              </TableSortLabel>
                            </TableCell>

                            <TableCell>Tx</TableCell>
                            <TableCell>Primary ID</TableCell>
                            <TableCell>Device / User</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Summary</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {pagedEvents.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={10} align="center">
                                <Typography variant="body2" color="text.secondary">
                                  No events. Select event types and click <b>Sync</b>.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            pagedEvents.map((ev, idx) => {
                              const pid = primaryId(ev);
                              const { device, user, amount, summary } = eventSummary(ev, { scale, safeFmtUnits });

                              return (
                                <TableRow key={(ev.transactionHash || "tx") + idx} hover>
                                  <TableCell>{ev.blockNumber}</TableCell>
                                  <TableCell>{ev.timestamp ? fmtTs(ev.timestamp) : "—"}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={ev.source} variant="outlined" />
                                  </TableCell>

                                  <TableCell>
                                    <Tooltip title="Open event details">
                                      <Chip
                                        clickable
                                        onClick={() => openDetails(ev)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") openDetails(ev);
                                        }}
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                          cursor: "pointer",
                                          maxWidth: { xs: 130, sm: 180, md: 260 },
                                          "& .MuiChip-label": { overflow: "hidden" },
                                        }}
                                        label={
                                          <Typography variant="body2" noWrap sx={{ maxWidth: "100%" }}>
                                            {ev.eventName}
                                          </Typography>
                                        }
                                      />
                                    </Tooltip>
                                  </TableCell>

                                  <TableCell sx={{ fontFamily: "monospace" }}>
                                    <Tooltip title="Open event details">
                                      <Button size="small" variant="text" onClick={() => openDetails(ev)} sx={{ fontFamily: "monospace", textTransform: "none" }}>
                                        {short(ev.transactionHash || "")}
                                      </Button>
                                    </Tooltip>
                                  </TableCell>

                                  <TableCell sx={{ fontFamily: "monospace" }}>
                                    <Tooltip title={pid || ""}>
                                      <span>{short(pid)}</span>
                                    </Tooltip>
                                  </TableCell>

                                  <TableCell sx={{ fontFamily: "monospace" }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <span>{device ? short(device) : "—"}</span>
                                      <span style={{ opacity: 0.7 }}>{user ? short(user) : ""}</span>
                                    </Box>
                                  </TableCell>

                                  <TableCell>{amount || ""}</TableCell>
                                  <TableCell sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {summary || ""}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="View details">
                                      <IconButton size="small" onClick={() => openDetails(ev)}>
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={sortedEvents.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      sx={{ borderTop: 1, borderColor: "divider" }}
                    />
                  </Paper>
                </>
              )}

              {/* Audit & Analytics tab */}
              {tab === 1 && (
                <>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Gavel color="primary" /> Audit Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Integrity checks + bridge settlement state machine + daily trends.
                    </Typography>
                  </Paper>

                  {queryFailures.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Some event queries failed during Sync. Audit surface may be incomplete for this time range.
                      <Box component="ul" sx={{ m: 0, mt: 1, pl: 3 }}>
                        {queryFailures.slice(0, 8).map((x) => (
                          <li key={x}>
                            <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{x}</Typography>
                          </li>
                        ))}
                        {queryFailures.length > 8 && (
                          <li>
                            <Typography variant="caption" color="text.secondary">
                              …and {queryFailures.length - 8} more
                            </Typography>
                          </li>
                        )}
                      </Box>
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <KpiCard title="Events loaded" value={auditMetrics.total.toLocaleString()} icon={<Insights />} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <KpiCard
                        title="Time span"
                        value={auditMetrics.minTs && auditMetrics.maxTs ? `${new Date(auditMetrics.minTs * 1000).toLocaleDateString()} → ${new Date(auditMetrics.maxTs * 1000).toLocaleDateString()}` : "—"}
                        icon={<TableChart />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <KpiCard
                        title="ReadingHash integrity"
                        value={`${auditMetrics.readingDup === 0 ? "OK" : `${auditMetrics.readingDup} dup`}`}
                        icon={auditMetrics.readingDup === 0 ? <CheckCircle color="success" /> : <ErrorIcon color="error" />}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {auditMetrics.readingTotal} hashes observed
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <KpiCard title="Missing timestamps" value={auditMetrics.missingTs.toLocaleString()} icon={<HourglassTop />} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                          Bridge settlement state
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="In-flight" value={auditMetrics.bridge.inFlight} color="warning.main" />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Finalized" value={auditMetrics.bridge.finalized} color="info.main" />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Success" value={auditMetrics.bridge.success} color="success.main" />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Fail" value={auditMetrics.bridge.fail} color="error.main" />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Burned" value={auditMetrics.bridge.burned} color="success.main" />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Refunds" value={auditMetrics.bridge.refunds} color="warning.main" />
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                          In-flight = TokensLocked without BridgeFinalized (in current dataset/time range).
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                          Lots & period anchoring
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Lot mints" value={auditMetrics.lots.lotMintDeltas} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Lot burns" value={auditMetrics.lots.lotBurns} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Transfers" value={auditMetrics.lots.lotTransfers} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Frozen" value={auditMetrics.lots.lotFrozen} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Unfrozen" value={auditMetrics.lots.lotUnfrozen} />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <MiniStat label="Device commits" value={auditMetrics.lots.deviceCommits} />
                          </Grid>
                        </Grid>
                        {viewerRole !== "user" && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            PeriodClosed / DevicePeriodCommitted / PeriodGlobalCommitted appear only for auditor/admin selection.
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                            Daily event volume
                          </Typography>
                          <Box sx={{ height: 260 }}>
                            <Line
                              data={{
                                labels: dailyBuckets.labels,
                                datasets: [
                                  {
                                    label: "Events/day",
                                    data: dailyBuckets.counts,
                                    borderColor: theme.palette.primary.main,
                                    tension: 0.25,
                                  },
                                ],
                              }}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                            Bridge finalized (success vs fail)
                          </Typography>
                          <Box sx={{ height: 260 }}>
                            <Line
                              data={{
                                labels: bridgeDaily.labels,
                                datasets: [
                                  { label: "Success", data: bridgeDaily.success, borderColor: theme.palette.success.main, tension: 0.25 },
                                  { label: "Fail", data: bridgeDaily.fail, borderColor: theme.palette.error.main, tension: 0.25 },
                                ],
                              }}
                              options={chartOptions}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Event Analytics mode (synced) */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                      <Box>
                        <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Analytics color="primary" /> Event Analytics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Analytics Mode is synced with Search selection and loaded events.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {!analyticsMode || analyticsList.length === 0 ? (
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No events found for <b>{analyticsMode || "—"}</b>. If you just changed selection, go back to Events tab and click <b>Sync</b>.
                      </Typography>
                    </Paper>
                  ) : analyticsMode !== "PowerOutAudit" ? (
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Basic analytics for <b>{analyticsMode}</b>: count and block trend.
                      </Typography>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                          <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Event count</Typography>
                            <Typography variant="h6">{analyticsList.length}</Typography>
                          </CardContent></Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Block trend</Typography>
                              <Box sx={{ height: 260 }}>
                                <Line
                                  data={{
                                    labels: makeSparseLabels(analyticsList, 10),
                                    datasets: [
                                      {
                                        label: "Block number",
                                        data: analyticsList.map((e) => e.blockNumber || 0),
                                        borderColor: theme.palette.primary.main,
                                        tension: 0.25,
                                      },
                                    ],
                                  }}
                                  options={chartOptions}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Paper>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                              Export / Import / Produced (kWh)
                            </Typography>
                            <Box sx={{ height: 280 }}>
                              <Line
                                data={{
                                  labels,
                                  datasets: [
                                    { label: "Export", data: powerOutSeries.ex, borderColor: theme.palette.success.main, tension: 0.25 },
                                    { label: "Import", data: powerOutSeries.im, borderColor: theme.palette.error.main, tension: 0.25 },
                                    { label: "Produced", data: powerOutSeries.produced, borderColor: theme.palette.primary.main, tension: 0.25 },
                                  ],
                                }}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                              Token Mint (scaled)
                            </Typography>
                            <Box sx={{ height: 280 }}>
                              <Line
                                data={{
                                  labels,
                                  datasets: [
                                    { label: "Ergon Mint", data: powerOutSeries.ergonMint, borderColor: theme.palette.info.main, tension: 0.25 },
                                    { label: "Carbon Mint", data: powerOutSeries.carbonMint, borderColor: theme.palette.warning.main, tension: 0.25 },
                                  ],
                                }}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                              Prices (AUD/kWh)
                            </Typography>
                            <Box sx={{ height: 280 }}>
                              <Line
                                data={{
                                  labels,
                                  datasets: [
                                    { label: "Ergon Price", data: powerOutSeries.ergonPrice, borderColor: theme.palette.warning.main, tension: 0.25 },
                                    { label: "Carbon Price", data: powerOutSeries.carbonPrice, borderColor: theme.palette.info.main, tension: 0.25 },
                                  ],
                                }}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                              Delta / Threshold
                            </Typography>
                            <Box sx={{ height: 280 }}>
                              <Line
                                data={{
                                  labels,
                                  datasets: [
                                    { label: "Δ kW", data: powerOutSeries.deltaKw, borderColor: theme.palette.primary.main, tension: 0.25 },
                                    { label: "Threshold kW", data: powerOutSeries.threshold, borderColor: theme.palette.error.main, tension: 0.25 },
                                  ],
                                }}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}

              {/* Diagnostics tab */}
              {tab === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BugReport /> Config
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}>
                        <div><b>SRC_WS</b>: {SRC_WS || "—"}</div>
                        <div><b>SRC_RPC</b>: {SRC_RPC || "—"}</div>
                        <div><b>SRC_NET_ID</b>: {SRC_NET_ID || "—"}</div>
                        <div><b>ENERGY_ADDR</b>: {ENERGY_ADDR || "—"}</div>
                        <div><b>CARBON_ADDR</b>: {CARBON_ADDR || "—"}</div>
                        <div><b>BRIDGE_ADDR</b>: {BRIDGE_ADDR || "—"}</div>
                        <div><b>ROLE</b>: {viewerRole}</div>
                        <div><b>VIEWER</b>: {viewerAddress || "—"}</div>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 2, height: 360, display: "flex", flexDirection: "column" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="h6">Logs</Typography>
                        <Button size="small" startIcon={<Clear />} onClick={() => setLog([])} disabled={!log.length}>Clear</Button>
                      </Box>
                      <Box
                        ref={logRef}
                        sx={{
                          flex: 1,
                          overflow: "auto",
                          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                          borderRadius: 1.5,
                          p: 1.5,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                          fontSize: 12,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {log.length ? log.join("\n") : "No logs yet. Click Sync."}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* NFT Audit tab */}
              {tab === 3 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Gavel /> NFT Provenance Audit
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        Audit one NFT mint transaction against on-chain logs, certificate fields, and metadata provenance fields.
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="RPC URL"
                            fullWidth
                            size="small"
                            value={nftAuditForm.rpcUrl}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, rpcUrl: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Tx Hash (optional if tokenId is provided)"
                            fullWidth
                            size="small"
                            value={nftAuditForm.txHash}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, txHash: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            label="Token ID (optional if txHash is provided)"
                            fullWidth
                            size="small"
                            value={nftAuditForm.tokenId}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, tokenId: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="From Block"
                            fullWidth
                            size="small"
                            value={nftAuditForm.fromBlock}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, fromBlock: e.target.value }))}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label="Vault Address"
                            fullWidth
                            size="small"
                            value={nftAuditForm.vaultAddress}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, vaultAddress: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="NFT Address"
                            fullWidth
                            size="small"
                            value={nftAuditForm.nftAddress}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, nftAddress: e.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="ETHCarbon Address"
                            fullWidth
                            size="small"
                            value={nftAuditForm.ethCarbonAddress}
                            onChange={(e) => setNftAuditForm((s) => ({ ...s, ethCarbonAddress: e.target.value }))}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          variant="contained"
                          startIcon={nftAuditLoading ? <HourglassTop /> : <Gavel />}
                          onClick={handleNftAudit}
                          disabled={nftAuditLoading || nftAuditV2Loading}
                        >
                          {nftAuditLoading ? "Auditing..." : "Run NFT Audit"}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={nftAuditV2Loading ? <HourglassTop /> : <Public />}
                          onClick={handleNftAuditV2}
                          disabled={nftAuditLoading || nftAuditV2Loading}
                        >
                          {nftAuditV2Loading ? "Auditing V2..." : "Run NFT Audit V2"}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <Paper sx={{ p: 2, minHeight: 420 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>Verdict</Typography>
                      {!nftAuditResult && !nftAuditLoading && !nftAuditV2Loading && (
                        <Alert severity="info">Run an audit to see verdict and consistency checks.</Alert>
                      )}

                      {(nftAuditLoading || nftAuditV2Loading) && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress />
                        </Box>
                      )}

                      {nftAuditResult && (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                            <Chip
                              icon={nftAuditResult.verdict === "PASS" ? <CheckCircle /> : nftAuditResult.verdict === "FAIL" ? <ErrorIcon /> : <HourglassTop />}
                              label={`Verdict: ${nftAuditResult.verdict || "UNKNOWN"}`}
                              color={nftAuditResult.verdict === "PASS" ? "success" : nftAuditResult.verdict === "FAIL" ? "error" : "warning"}
                              variant="filled"
                            />
                            <Chip size="small" variant="outlined" label={`Token #${nftAuditResult.tokenId ?? "?"}`} />
                            <Chip size="small" variant="outlined" label={short(nftAuditResult.txHash || "", 10) || "No txHash"} />
                          </Box>

                          {nftAuditResult.auditVersion === "v2" && (
                            <HepekAuditPanel result={nftAuditResult} />
                          )}

                          {nftAuditResult.mintProvenance && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Mint Provenance Split</Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`lotTotalOnBridgeWei: ${nftAuditResult.mintProvenance.lotTotalOnBridgeWei || "0"}`}
                                  sx={{ fontFamily: "monospace" }}
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`burnedInThisMintWei: ${nftAuditResult.mintProvenance.burnedInThisMintWei || "0"}`}
                                  sx={{ fontFamily: "monospace" }}
                                />
                              </Box>
                              {!!nftAuditResult.mintProvenance.deviceShareThisMint?.length && (
                                <Box sx={{ mt: 1, fontSize: 12 }}>
                                  {nftAuditResult.mintProvenance.deviceShareThisMint.map((d, idx) => (
                                    <Typography key={`${d.device}-${idx}`} variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                                      {`${d.device}: ${d.burnedInThisMintWei} wei (${d.sharePercent}%)`}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}

                          {!!nftAuditResult.bridgeLineage?.length && (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Source to Destination Bridge Lineage</Typography>
                              {nftAuditResult.bridgeLineage.map((row, idx) => {
                                const srcLock = row?.sourceLock || null;
                                const srcV2 = row?.sourceEvents?.initiatedV2 || null;
                                const srcV3 = row?.sourceEvents?.initiatedV3 || null;
                                const srcLots = row?.sourceEvents?.initiatedLots || null;
                                const dstMint = row?.destinationBridgeMint || null;
                                const lotCtx = row?.lotContext || null;
                                const srcBridgeTx = srcV3?.txHash || srcV2?.txHash || srcLots?.txHash || "-";
                                const srcV1TxRaw = String(srcLock?.v1TxHash || srcV2?.v1TxHash || "");
                                const srcV1Tx = srcV1TxRaw && !/^0x0{64}$/i.test(srcV1TxRaw) ? srcV1TxRaw : "-";
                                const lotV1TxRaw = String(lotCtx?.v1TxHash || "");
                                const lotV1Tx = lotV1TxRaw && !/^0x0{64}$/i.test(lotV1TxRaw) ? lotV1TxRaw : "-";
                                return (
                                  <Box key={`${row?.messageId || "mid"}-${idx}`} sx={{ mb: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      messageId: {String(row?.messageId || "-")}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      classification: {String(row?.classification || "-")}
                                    </Typography>
                                    {row?.note && (
                                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", wordBreak: "break-word" }}>
                                        note: {String(row.note)}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      srcDevice: {String(srcLock?.device || srcV2?.device || "-")}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      srcUser: {String(srcLock?.user || srcV2?.user || "-")}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      srcBridgeTx: {String(srcBridgeTx)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      srcV1TxHash: {String(srcV1Tx)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all" }}>
                                      dstBridgeMintTx: {String(dstMint?.txHash || "-")}
                                    </Typography>
                                    {!!lotCtx && (
                                      <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", wordBreak: "break-all", color: "text.secondary" }}>
                                        lotContext: device={String(lotCtx.device || "-")}, user={String(lotCtx.user || "-")}, v1TxHash={String(lotV1Tx)}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {nftAuditResult.auditVersion !== "v2" && (
                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 290, overflowX: "hidden" }}>
                            <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Check</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Expected</TableCell>
                                  <TableCell>Actual</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(nftAuditResult.checks || []).map((c, idx) => (
                                  <TableRow key={`${c.key || "check"}-${idx}`}>
                                    <TableCell sx={{ width: "36%", whiteSpace: "normal", wordBreak: "break-word" }}>{c.label || c.key}</TableCell>
                                    <TableCell sx={{ width: "12%" }}>
                                      <Chip
                                        size="small"
                                        label={String(c.status || "unknown").toUpperCase()}
                                        color={checkColor(c.status)}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell sx={{ width: "26%", fontFamily: "monospace", fontSize: 12, whiteSpace: "normal", wordBreak: "break-all" }}>{String(c.expected ?? "—")}</TableCell>
                                    <TableCell sx={{ width: "26%", fontFamily: "monospace", fontSize: 12, whiteSpace: "normal", wordBreak: "break-all" }}>{String(c.actual ?? "—")}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          )}
                        </>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Geolocations tab */}
              {tab === 4 && (
                <Box sx={{ height: 500, width: "100%", mt: 2 }}>
                  <GeolocationsMap devices={deviceLocations} />
                </Box>
              )}


            </Box>
          </Paper>

          {/* Detail dialog */}
          <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
            {selectedEvent && (
              <>
                <DialogTitle sx={{ pb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={selectedEvent.source} variant="outlined" size="small" />
                    <Chip label={selectedEvent.eventName} color="primary" variant="outlined" size="small" />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Block {selectedEvent.blockNumber} • {selectedEvent.timestamp ? fmtTs(selectedEvent.timestamp) : "—"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {/* SITE ID CHIP */}
                    {(() => {
                      const payload = selectedEvent.payload || {};
                      let siteId = payload.site_id || payload.siteId;
                      if (!siteId && payload.device_id) {
                        // Try to extract site from device_id (e.g. M002_NSW_2101_Narrabeen)
                        const parts = payload.device_id.split("_");
                        if (parts.length >= 3) {
                          siteId = parts.slice(1).join("_");
                        }
                        siteId = "Mxxx-NSW-2099-Dee-Why"; // Fallback for testing
                      }
                      return siteId ? (
                        <Chip label={siteId} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 700, fontSize: 16, mr: 1 }} />
                      ) : null;
                    })()}
                    <IconButton onClick={() => setDetailOpen(false)}><Close /></IconButton>
                  </Box>
                </DialogTitle>


                <DialogContent>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 24,
                      minWidth: 220,
                      maxWidth: 320,
                      p: 2,
                      background: "none",
                      boxShadow: "none",
                      border: "none",
                      zIndex: 2,
                      pointerEvents: "none"
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mt: 4, mb: 1, fontWeight: 700 }}>
                      {actionTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                      {actionDescription}
                    </Typography>
                  </Box>

                  {(() => {
                    const a = selectedEvent.args || {};
                    const tx = selectedEvent.transactionHash || "";
                    const readingHash = a.readingHash;

                    // Hash calculation helper
                    // NOTE: uses shared calcReadingHash(payload) defined in the component (READING_V1)
                    const calcReadingHash = (obj) => {
                      const m = obj.meter_reading;
                      const timestamp = parseAnyTimestampToEpochSeconds(obj.timestamp);
                      const audit_payload = {
                        "schema": "READING_V1",
                        "device_id": obj.device_id,
                        "timestamp": timestamp,
                        "direction": m.direction,
                        "produced": m.produced_power,
                        "consumed": m.consumed_power,
                        "factor": m.FACTOR,
                        "prev_hash": m.previous_reading_hash
                      }
                      const audit_payload_V2 = {
                        hepek: m.hepek,
                        bucketTs: timestamp,            // must be bucketed epoch int
                        dir: m.direction,
                        produced: m.produced_power,
                        exported: m.exported_power,
                        consumed: m.consumed_power,
                        factor: m.FACTOR,
                        prevHash: m.previous_reading_hash
                      }
                      //verifyReadingHashV2(m, m.reading_hash)
                      const str = JSON.stringify(audit_payload, Object.keys(audit_payload).sort());
                      const str_V2 = JSON.stringify(audit_payload_V2, Object.keys(audit_payload_V2).sort());
                      // Use ethers.js keccak256 for EVM-style hash (or fallback to SHA256 if not available)
                      try {
                        const text = toUtf8Bytes(str);
                        const text_V2 = toUtf8Bytes(str_V2);
                        const hash = keccak256(text);
                        const hash_V2 = keccak256(text_V2);
                        return hash, hash_V2;
                      } catch (e) {
                        if (window.crypto && window.crypto.subtle) {
                          // SHA256 fallback
                          return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then(buf => {
                            return '0x' + Array.from(new Uint8Array(buf)).map(x => x.toString(16).padStart(2, '0')).join('');
                          });
                        } else {
                          console.warn("SHA256 not available in this browser");
                          return null;
                        }
                      }
                    };

                    // Fetch reading by hash
                    const fetchReading = async () => {
                      setLoadingReading(true);
                      setReadingError(null);
                      setReadingData(null);
                      setReadingStatus(null);
                      try {
                        addLog && addLog(`Fetch & verify readingHash: started for ${readingHash}`);
                        const res = await fetch(`${READING_API_BASE}/device/reading/hash/${readingHash}`);
                        if (!res.ok) throw new Error('Not found');
                        const data = await res.json();
                        setReadingData(data);
                        // Calculate hash and compare
                        const payload = data.payload;
                        let hash, hash_V2 = await calcReadingHash(payload);
                        const verifiedV2 = verifyReadingHashV2(payload, readingHash);

                        if (hash && hash.then) hash = await hash; // handle promise if SHA256
                        if (hash && hash.toLowerCase() === readingHash.toLowerCase()) {
                          addLog && addLog(`Fetch & verify readingHash: match for ${readingHash}`);
                          setReadingStatus('ok');
                        } else if (hash_V2 && hash_V2.toLowerCase() === readingHash.toLowerCase()) {
                          addLog && addLog(`Fetch & verify readingHash: match V2 for ${readingHash}`);
                          setReadingStatus('ok');
                        } else if (verifiedV2) {
                          addLog && addLog(`Fetch & verify readingHash: match V2 verified fn for ${readingHash}`);
                          setReadingStatus('ok');
                        } else {
                          addLog && addLog(`Fetch & verify readingHash: MISMATCH for ${readingHash} (calculated: ${hash})`);
                          setReadingStatus('fail');
                        }
                      } catch (e) {
                        setReadingError(e.message);
                        addLog && addLog(`Fetch & verify readingHash: ERROR for ${readingHash}: ${e.message}`);
                      } finally {
                        setLoadingReading(false);
                      }
                    };

                    const fields = [
                      ["tx hash", tx],
                      ["readingHash", a.readingHash],
                      ["provenanceHash", a.provenanceHash],
                      ["messageId", a.messageId],
                      ["lotsHash", a.lotsHash],
                      ["payloadHash", a.payloadHash],
                      ["proofHash", a.proofHash],
                      ["sourceHash", a.sourceHash],
                      ["lotId", a.lotId || a.id],
                    ].filter(([, v]) => v);

                    const senderState =
                      txSenderLoading ? "pending"
                      : txSenderStatus === "ok" ? true
                      : txSenderStatus === "fail" ? false
                      : txSenderStatus === "error" ? "error"
                      : null;

                    return (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary">Copy shortcuts</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                              {fields.map(([label, value]) => (
                                <Button
                                  key={label}
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ContentCopy fontSize="small" />}
                                  onClick={() => copyToClipboard(label, value)}
                                >
                                  {label}
                                </Button>
                              ))}
                            </Box>
                            {readingHash && (
                              <Box sx={{ mt: 2 }}>
                                <VerifyActionButton
                                  size="small"
                                  variant="contained"
                                  color="info"
                                  onClick={() => {
                                    setActionTitle("");
                                    setActionDescription("");
                                    fetchReading();
                                  }}
                                  disabled={loadingReading}
                                  //status={senderState}   // "pending" | true | false | "unknown" | "error" | null
                                >
                                  Fetch & verify readingHash
                                </VerifyActionButton>
                                {loadingReading && <span style={{ marginLeft: 8 }}>Loading…</span>}
                                {readingStatus === 'ok' && <Chip label="MATCH" color="success" sx={{ ml: 2 }} />}
                                {readingStatus === 'fail' && <Chip label="MISMATCH" color="error" sx={{ ml: 2 }} />}
                                {readingError && <Typography color="error" variant="caption" sx={{ ml: 2 }}>{readingError}</Typography>}
                                {tx && (a.device || a.hepekID) && (
                                  <>
                                    <VerifyActionButton
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      sx={{ ml: 2 }}
                                      onClick={() => verifyTxSender(tx, a.device || a.hepekID)}
                                      disabled={txSenderLoading}
                                      status={senderState}   // "pending" | true | false | "unknown" | "error" | null
                                    >
                                      Verify tx sender
                                    </VerifyActionButton>
                                  </>
                                )}

                                {/* Line 2782 omitted */}
                                <AuditChainChase readingHash={readingHash} deviceAddress={a.device || a.hepekID || ""} txHash={tx} />
                              </Box>
                            )}
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Paper sx={{ p: 2, minHeight: 220 }}>
                            <Typography variant="subtitle2" color="text.secondary">Decoded args</Typography>
                            <Box sx={{ mt: 1, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {JSON.stringify(stringifyArgs(a), null, 2)}
                            </Box>
                            {readingData && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">Fetched reading</Typography>
                                <Box
                                  sx={(theme) => ({
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor:
                                      readingStatus === 'ok'
                                        ? (theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.15)' : '#e8f5e9')
                                        : (theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.15)' : '#ffebee'),
                                  })}
                                >
                                  {JSON.stringify(readingData, null, 2)}
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        </Grid>

                        <Grid item xs={12}>
                          <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">Transaction Audit V2</Typography>
                              {txAuditResult?.verdict && (
                                <Chip
                                  size="small"
                                  color={txAuditResult.verdict === "PASS" ? "success" : txAuditResult.verdict === "FAIL" ? "error" : "warning"}
                                  label={txAuditResult.verdict}
                                />
                              )}
                            </Box>

                            {txAuditLoading && <LinearProgress />}
                            {!txAuditLoading && txAuditError && <Alert severity="warning">{txAuditError}</Alert>}
                            {!txAuditLoading && !txAuditError && !txAuditResult && (
                              <Alert severity="info">No HEPEK provenance transaction audit loaded.</Alert>
                            )}
                            {txAuditResult && <HepekAuditPanel result={txAuditResult} compact />}
                          </Paper>
                        </Grid>
                      </Grid>
                    );
                  })()}

                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<GetApp />}
                    onClick={() => {
                      const file = `event-${selectedEvent.source}-${selectedEvent.eventName}-${selectedEvent.blockNumber}.json`;
                      downloadBlob(file, JSON.stringify(stringifyArgs(selectedEvent), null, 2), "application/json;charset=utf-8");
                    }}
                  >
                    Export JSON
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Snackbar */}
          <Snackbar open={snack.open} autoHideDuration={2400} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
            <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
              {snack.msg}
            </Alert>
          </Snackbar>
        </Box>
      </LogContext.Provider>
    </ProviderContext.Provider>
  );
}

/* --------------------- Helpers / utils --------------------- */



export default ProvenanceHome;
